import environment from "environment";
import i18n from "i18n";
import IBAN from "iban";
import _ from "lodash";
import moment from "moment";
import { isValidPhoneNumber } from "react-phone-number-input";
import TipoRichiedenteModel from "stores/tipo-richiedente/models/TipoRichiedenteModel";
import * as Yup from "yup";
import { ObjectSchema, ValidationError } from "yup";
import FormatUtility, { ClonedObject } from "./FormatUtility";

export type ValidationResult = {
    message: string;
    invalid: boolean;
};

export type ValidationContainer<Object> = ClonedObject<Object, ValidationResult>;

export type ValidationSchema<Object extends object> = ObjectSchema<RecursivePartial<Object>>;

export default abstract class ValidationUtility {
    private constructor() { }

    private static setLocale() {
        Yup.setLocale({
            mixed: {
                default: i18n.t("error_global_default"),
                required: i18n.t("error_global_required"),
            },
            string: {
                min: i18n.t("error_string_min"),
                max: i18n.t("error_string_max"),
            },
            number: {
                min: i18n.t("error_number_min"),
                max: i18n.t("error_number_max"),
                integer: i18n.t("error_number_integer"),
            },
            date: {
                min: i18n.t("error_date_min"),
                max: i18n.t("error_date_max"),
            },
        });
    }

    public static setValidationContainer<T>(viewModel: T): ValidationContainer<T> {
        let propContent = { message: "", invalid: false } as ValidationResult;
        let replaceValuesFunction = () => {
            return propContent;
        };
        return FormatUtility.cloneObject<T, ValidationResult>(
            viewModel,
            replaceValuesFunction
        ) as ValidationContainer<T>;
    }

    private static resetValidationContainer<T>(errors: ValidationContainer<T>): ValidationContainer<T> {
        // reset all errors
        let replaceValuesFunction = (val: any) => {
            switch (val.constructor.name) {
                case "String":
                    return ""; //message
                case "Boolean":
                    return false; //invalid
            }
            return val;
        };
        return FormatUtility.cloneObject<ValidationContainer<T>, ValidationResult>(
            errors,
            replaceValuesFunction
        ) as ValidationContainer<T>;
    }

    public static checkValidity<T>(errors: ValidationContainer<T>) {
        let result: boolean[] = [];
        FormatUtility.getNestedFieldsValues(errors, "invalid", result);
        if (result.some((x) => x === true)) {
            return false;
        } else {
            return true;
        }
    }

    public static async validateSchema<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        viewModel: T,
        fieldName?: string,
        fieldValue?: any
    ) {
        // check if schema contains field
        if (fieldName !== undefined) {
            let field = _.get(schema.fields, fieldName.replace(".", ".fields.").replace(/\[(.*?)\]/g, ".innerType"));
            if (field === undefined) {
                return errors;
            }
        }

        // if value passed, update viewModel
        if (fieldName !== undefined && fieldValue !== undefined) {
            _.set(viewModel, fieldName, fieldValue);
        }

        // replace empty strings with undefined
        let replaceValuesFunction = (val: any) => {
            if (typeof val === "string" && val === "") {
                return undefined;
            }
            return val;
        };
        viewModel = FormatUtility.cloneObject<T, any>(viewModel, replaceValuesFunction) as T;

        // validate schema (get all errors)
        await schema
            .validate(viewModel, { abortEarly: false })
            .then(() => {
                // reset all errors
                errors = ValidationUtility.resetValidationContainer(errors);
            })
            .catch((err: ValidationError) => {
                // set errors
                if (fieldName !== undefined) {
                    // set current field error
                    let fieldErrors = err.inner.filter((x) => x.path === fieldName);
                    let fieldMessage = fieldErrors.length > 0 ? fieldErrors[0].message : "";
                    let result = {
                        message: fieldMessage,
                        invalid: fieldMessage !== "",
                    } as ValidationResult;
                    _.set(errors, fieldName, result);
                } else {
                    // reset all errors
                    errors = ValidationUtility.resetValidationContainer(errors);

                    // set all fields errors
                    err.inner.forEach((error) => {
                        let fieldName = error.path;
                        let fieldMessage = error.message;
                        // ignore if an error is already setted
                        let current = _.get(errors, fieldName);
                        if (current) {
                            if (current.invalid) {
                                return;
                            }
                        }
                        let result = {
                            message: fieldMessage,
                            invalid: fieldMessage !== "",
                        } as ValidationResult;
                        _.set(errors, fieldName, result);
                    });
                }
            });
        // return new errors object
        return errors;
    }

    // #region dismessi il 04/06/2020
    private static getFieldName(target: EventTarget & HTMLFormElement) {
        let fieldName =
            target.name ||
            target.id ||
            (target.parentElement?.parentElement?.nextElementSibling as HTMLInputElement).name ||
            (target.parentElement?.parentElement?.nextElementSibling as HTMLInputElement).id ||
            "";
        return fieldName;
    }

    private static async validateField<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        name: string,
        value: string,
        viewModel: T
    ) {
        let result = {
            message: "",
            invalid: false,
        } as ValidationResult;
        let field = _.get(schema.fields, name.replace(".", ".fields."));
        if (field !== undefined) {
            await field
                .validate(value === "" ? undefined : value, { context: viewModel })
                .then(() => {
                    result.message = "";
                    result.invalid = false;
                })
                .catch((err: any) => {
                    result.message = err.message;
                    result.invalid = true;
                });
            _.set(errors, name, result);
        }
        return errors;
    }

    private static async validateSchemaIteration<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        viewModel: T,
        obj: ValidationSchema<T> = schema,
        path: string = "",
        fieldContainer: string = "fields"
    ): Promise<ValidationContainer<T>> {
        if (fieldContainer in obj) {
            errors = await ValidationUtility.validateSchemaIteration(schema, errors, viewModel, obj[fieldContainer]);
        } else {
            let fieldsNames = Object.getOwnPropertyNames(obj);
            for (let i = 0; i < fieldsNames.length; i++) {
                let fieldName = fieldsNames[i];
                if (fieldContainer in obj[fieldName]) {
                    let newPath = "";
                    if (path.length > 0) {
                        newPath += path + ".";
                    }
                    newPath += fieldName;
                    errors = await ValidationUtility.validateSchemaIteration(
                        schema,
                        errors,
                        viewModel,
                        obj[fieldName][fieldContainer],
                        newPath
                    );
                } else {
                    let newFieldName = "";
                    if (path.length > 0) {
                        newFieldName += path + ".";
                    }
                    newFieldName += fieldName;
                    let value = _.get(viewModel, newFieldName);
                    if (value !== undefined) {
                        errors = await ValidationUtility.validateField(schema, errors, newFieldName, value, viewModel);
                    }
                }
            }
        }
        return errors;
    }

    private static async validateForm<T extends object>(
        schema: ValidationSchema<T>,
        errors: ValidationContainer<T>,
        viewModel: T
    ) {
        return await ValidationUtility.validateSchemaIteration<T>(schema, errors, viewModel);
    }
    // #endregion dismessi il 04/06/2020

    public static getSchemaPreventivo<T extends object>(
        tipiRichiedenti: TipoRichiedenteModel[],
        campiBoxPreventivo: boolean,
        attivaCampiNomeCognome: boolean,
        showSliders: boolean
    ): ValidationSchema<T> {
        ValidationUtility.setLocale();
        if (campiBoxPreventivo === false) {
            return Yup.object().shape({
                importoRichiesto: Yup.string()
                    .required(i18n.t("error_required_importo_richiesto"))
                    .notOneOf(["", "0", "0,00"], i18n.t("error_required_importo_richiesto"))
                    .test("format", i18n.t("error_invalid_importo_richiesto"), (val) => {
                        return this.checkIfImportoIsValid(val);
                    })
                    .test(
                        "min",
                        i18n.t("error_min_importo_richiesto").replace("${1}", FormatUtility.currency(1000)),
                        (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value != null) {
                                return this.checkMinNumber(value, 1000);
                            }
                            return false;
                        }
                    )
                    .test(
                        "max",
                        i18n.t("error_max_importo_richiesto").replace("${1}", FormatUtility.currency(75000)),
                        (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value !== undefined) {
                                return this.checkMaxNumber(value, 75000);
                            }
                            return false;
                        }
                    )
                    .label(i18n.t("error_field_importo_richiesto")),
                codiceTipoRichiedente: Yup.string()
                    .required(i18n.t("error_required_tipo_richiedente"))
                    .label(i18n.t("error_field_tipo_richiedente")),
                dataNascita: Yup.string()
                    .when("codiceTipoRichiedente", {
                        is: (codiceTipoRichiedente) => {
                            return this.checkIfCampoIsRequiredByTipoRichiedente(
                                tipiRichiedenti,
                                codiceTipoRichiedente,
                                "DATA_NASCITA"
                            );
                        },
                        then: Yup.string()
                            .required(i18n.t("error_required_data_nascita"))
                            .test("format", i18n.t("error_global_date_format"), (val) => {
                                return this.checkIfDateIsValidOrUndefined(val);
                            })
                            .test("min", i18n.t("error_min_data_nascita").replace("${1}", (86).toString()), (val) => {
                                let minDate = new Date();
                                minDate.setFullYear(minDate.getFullYear() - 86);
                                return this.checkMinDate(val, minDate);
                            })
                            .test("futura", i18n.t("error_futura_data_nascita"), (val) => {
                                let maxDate = new Date();
                                return this.checkMaxDate(val, maxDate);
                            })
                            .test("minorenne", i18n.t("error_minorenne_data_nascita"), (val) => {
                                let maxDate = new Date();
                                maxDate.setFullYear(maxDate.getFullYear() - 18);
                                return this.checkMaxDate(val, maxDate);
                            }),
                    })
                    .label(i18n.t("error_field_data_nascita")),
                importoNettoRedditoMensile: Yup.string()
                    .when("codiceTipoRichiedente", {
                        is: (codiceTipoRichiedente) => {
                            return this.checkIfCampoIsRequiredByTipoRichiedente(
                                tipiRichiedenti,
                                codiceTipoRichiedente,
                                "IMPORTO_NETTO_MENSILE"
                            );
                        },
                        then: Yup.string()
                            .required(i18n.t("error_required_importo_netto_mensile"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_importo_netto_mensile"))
                            .test("format", i18n.t("error_invalid_importo_netto_mensile"), (val) => {
                                return this.checkIfImportoIsValid(val);
                            })
                            .test(
                                "min",
                                i18n.t("error_min_importo_netto_mensile").replace("${1}", FormatUtility.currency(500)),
                                (val) => {
                                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                                    if (value != null) {
                                        return this.checkMinNumber(value, 500);
                                    }
                                    return false;
                                }
                            )
                            .test(
                                "max",
                                i18n
                                    .t("error_max_importo_netto_mensile")
                                    .replace("${1}", FormatUtility.currency(100000)),
                                (val) => {
                                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                                    if (value !== undefined) {
                                        //2147483647
                                        return this.checkMaxNumber(value, 100000);
                                    }
                                    return false;
                                }
                            ),
                    })
                    .label(i18n.t("error_field_importo_netto_mensile")),
                dataAssunzione: Yup.string()
                    .when("codiceTipoRichiedente", {
                        is: (codiceTipoRichiedente) => {
                            return this.checkIfCampoIsRequiredByTipoRichiedente(
                                tipiRichiedenti,
                                codiceTipoRichiedente,
                                "DATA_ASSUNZIONE"
                            );
                        },
                        then: Yup.string()
                            .required(i18n.t("error_required_data_assunzione"))
                            .test("format", i18n.t("error_global_date_format"), (val) => {
                                return this.checkIfDateIsValidOrUndefined(val);
                            })
                            .test(
                                "min",
                                i18n.t("error_min_data_assunzione").replace("${1}", (86).toString()),
                                (val) => {
                                    let minDate = new Date();
                                    minDate.setFullYear(minDate.getFullYear() - 86);
                                    return this.checkMinDate(val, minDate);
                                }
                            )
                            .test("futura", i18n.t("error_futura_data_assunzione"), (val) => {
                                let maxDate = new Date();
                                return this.checkMaxDate(val, maxDate);
                            }),
                    })
                    .label(i18n.t("error_field_data_assunzione")),
                importoRata: Yup.number()
                    .when("importoNettoRedditoMensile", {
                        is: () => {
                            return showSliders;
                        },
                        then: Yup.number().required(i18n.t("error_required_rata")).min(50).integer(),
                    })
                    .label(i18n.t("error_field_rata")),
                durata: Yup.number()
                    .when("importoNettoRedditoMensile", {
                        is: () => {
                            return showSliders;
                        },
                        then: Yup.number().required(i18n.t("error_required_durata")).min(24).max(120).integer(),
                    })
                    .label(i18n.t("error_field_durata")),
            });
        } else {
            return Yup.object().shape({
                importoRichiesto: Yup.string()
                    .required(i18n.t("error_required_importo_richiesto"))
                    .notOneOf(["", "0", "0,00"], i18n.t("error_required_importo_richiesto"))
                    .test("format", i18n.t("error_invalid_importo_richiesto"), (val) => {
                        return this.checkIfImportoIsValid(val);
                    })
                    .test(
                        "min",
                        i18n.t("error_min_importo_richiesto").replace("${1}", FormatUtility.currency(1000)),
                        (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value != null) {
                                return this.checkMinNumber(value, 1000);
                            }
                            return false;
                        }
                    )
                    .test(
                        "max",
                        i18n.t("error_max_importo_richiesto").replace("${1}", FormatUtility.currency(75000)),
                        (val) => {
                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                            if (value !== undefined) {
                                return this.checkMaxNumber(value, 75000);
                            }
                            return false;
                        }
                    )
                    .label(i18n.t("error_field_importo_richiesto")),
                codiceTipoRichiedente: Yup.string()
                    .required(i18n.t("error_required_tipo_richiedente"))
                    .label(i18n.t("error_field_tipo_richiedente")),
                dataNascita: Yup.string()
                    .when("codiceTipoRichiedente", {
                        is: (codiceTipoRichiedente) => {
                            return this.checkIfCampoIsRequiredByTipoRichiedente(
                                tipiRichiedenti,
                                codiceTipoRichiedente,
                                "DATA_NASCITA"
                            );
                        },
                        then: Yup.string()
                            .required(i18n.t("error_required_data_nascita"))
                            .test("format", i18n.t("error_global_date_format"), (val) => {
                                return this.checkIfDateIsValidOrUndefined(val);
                            })
                            .test("min", i18n.t("error_min_data_nascita").replace("${1}", (86).toString()), (val) => {
                                let minDate = new Date();
                                minDate.setFullYear(minDate.getFullYear() - 86);
                                return this.checkMinDate(val, minDate);
                            })
                            .test("futura", i18n.t("error_futura_data_nascita"), (val) => {
                                let maxDate = new Date();
                                return this.checkMaxDate(val, maxDate);
                            })
                            .test("minorenne", i18n.t("error_minorenne_data_nascita"), (val) => {
                                let maxDate = new Date();
                                maxDate.setFullYear(maxDate.getFullYear() - 18);
                                return this.checkMaxDate(val, maxDate);
                            }),
                    })
                    .label(i18n.t("error_field_data_nascita")),
                importoNettoRedditoMensile: Yup.string()
                    .when("codiceTipoRichiedente", {
                        is: (codiceTipoRichiedente) => {
                            return this.checkIfCampoIsRequiredByTipoRichiedente(
                                tipiRichiedenti,
                                codiceTipoRichiedente,
                                "IMPORTO_NETTO_MENSILE"
                            );
                        },
                        then: Yup.string()
                            .required(i18n.t("error_required_importo_netto_mensile"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_importo_netto_mensile"))
                            .test("format", i18n.t("error_invalid_importo_netto_mensile"), (val) => {
                                return this.checkIfImportoIsValid(val);
                            })
                            .test(
                                "min",
                                i18n.t("error_min_importo_netto_mensile").replace("${1}", FormatUtility.currency(500)),
                                (val) => {
                                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                                    if (value != null) {
                                        return this.checkMinNumber(value, 500);
                                    }
                                    return false;
                                }
                            )
                            .test(
                                "max",
                                i18n
                                    .t("error_max_importo_netto_mensile")
                                    .replace("${1}", FormatUtility.currency(100000)),
                                (val) => {
                                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                                    if (value !== undefined) {
                                        //2147483647
                                        return this.checkMaxNumber(value, 100000);
                                    }
                                    return false;
                                }
                            ),
                    })
                    .label(i18n.t("error_field_importo_netto_mensile")),
                dataAssunzione: Yup.string()
                    .when("codiceTipoRichiedente", {
                        is: (codiceTipoRichiedente) => {
                            return this.checkIfCampoIsRequiredByTipoRichiedente(
                                tipiRichiedenti,
                                codiceTipoRichiedente,
                                "DATA_ASSUNZIONE"
                            );
                        },
                        then: Yup.string()
                            .required(i18n.t("error_required_data_assunzione"))
                            .test("format", i18n.t("error_global_date_format"), (val) => {
                                return this.checkIfDateIsValidOrUndefined(val);
                            })
                            .test(
                                "min",
                                i18n.t("error_min_data_assunzione").replace("${1}", (86).toString()),
                                (val) => {
                                    let minDate = new Date();
                                    minDate.setFullYear(minDate.getFullYear() - 86);
                                    return this.checkMinDate(val, minDate);
                                }
                            )
                            .test("futura", i18n.t("error_futura_data_assunzione"), (val) => {
                                let maxDate = new Date();
                                return this.checkMaxDate(val, maxDate);
                            }),
                    })
                    .label(i18n.t("error_field_data_assunzione")),
                importoRata: Yup.number()
                    .when("importoNettoRedditoMensile", {
                        is: () => {
                            return showSliders;
                        },
                        then: Yup.number().required(i18n.t("error_required_rata")).min(50).integer(),
                    })
                    .label(i18n.t("error_field_rata")),
                durata: Yup.number()
                    .when("importoNettoRedditoMensile", {
                        is: () => {
                            return showSliders;
                        },
                        then: Yup.number().required(i18n.t("error_required_durata")).min(24).max(120).integer(),
                    })
                    .label(i18n.t("error_field_durata")),

                informazioniUtente: Yup.object().shape({
                    nome: Yup.string()
                        .when("attivaCampiNomeCognome", {
                            is: (attivaCampiNomeCognome) => {
                                return true;
                            },
                            then: Yup.string().required(i18n.t("registrazione_required_nome")),
                        })
                        //.required(i18n.t("registrazione_required_email"))
                        .label("registrazione_field_nome")
                        .default(""),
                    cognome: Yup.string()
                        .when("attivaCampiNomeCognome", {
                            is: (attivaCampiNomeCognome) => {
                                return true;
                            },
                            then: Yup.string().required(i18n.t("registrazione_required_cognome")),
                        })
                        //.required(i18n.t("registrazione_required_email"))
                        .label("registrazione_field_cognome")
                        .default(""),
                    email: Yup.string()
                        .when("campiBoxPreventivo", {
                            is: (campiBoxPreventivo) => {
                                return true;
                            },
                            then: Yup.string().required(i18n.t("registrazione_required_email")),
                        })
                        .email(i18n.t("registrazione_invalid_email"))
                        //.required(i18n.t("registrazione_required_email"))
                        .label("registrazione_field_email")
                        .default(""),
                    numeroTelefono: Yup.string()
                        .when("campiBoxPreventivo", {
                            is: (campiBoxPreventivo) => {
                                return true;
                            },
                            then: Yup.string().required(i18n.t("error_required_telefono")),
                        })
                        .test("format", i18n.t("error_invalid_telefono"), (val: string) => {
                            if (val === undefined) {
                                return true;
                            }
                            return ValidationUtility.checkPhoneNumber(val);
                        })
                        .label("Telefono"),
                    privacy: Yup.array().of(
                        Yup.object().shape({
                            descrizione: Yup.string(),
                            isObbligatorio: Yup.boolean(),
                            codice: Yup.string(),
                            fgMostraLanding: Yup.string(),
                            isAccettato: Yup.boolean()
                                .when("isObbligatorio", {
                                    is: (isObbligatorio) => {
                                        // return ValidationUtility.checkIfCampoIsRequiredByConfiguration(campiBoxPreventivo, isObbligatorio, fgMostraLanding);
                                        return true;
                                    },
                                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                                        fieldSchema.test(
                                            "obbligatorio",
                                            i18n.t("registrazione_required_consenso_privacy"),
                                            (val) => {
                                                return FormatUtility.castValue(val, true) === true;
                                            }
                                        ),
                                })
                                .label(i18n.t("registrazione_field_consenso_privacy")),
                        })
                    ),
                }),
            });
        }
    }

    public static getSchemaRichiestaPreventivo<T extends object>(
        visualizzaAltriImpegniNelTabPrestiti: boolean
    ): ValidationSchema<T> {
        ValidationUtility.setLocale();

        return Yup.object().shape({
            categoriaAmministrativa: Yup.string()
                .required(i18n.t("error_required_categoriaAmministrativa"))
                .label("Categoria Amministrativa"),
            amministrazione: Yup.string(),
            fgAltriFinanziamenti: Yup.string()
                .required(i18n.t("error_required_fgAltriFinanziamenti"))
                .label("Altri Finanziamenti"),
            altriImpegni: Yup.array().when("fgAltriFinanziamenti", {
                is: "S",
                then: Yup.array().of(
                    Yup.object().shape({
                        tipologiaImpegno: Yup.string()
                            .required(i18n.t("error_required_tipoImpegno"))
                            .label("Tipologia Impegno"),
                        // fgPresenzaDati: Yup.string()
                        //     .required(i18n.t("error_required_fgPresenzaDati"))
                        //     .label("Nuovo Impegno"),
                        // estinzione: Yup.string()
                        //     .when("fgPresenzaDati", {
                        //         is: "S",
                        //         then: Yup.string().required(i18n.t("error_required_fgEstinzione")),
                        // .test("checkTipoImpegno", i18n.t("error_invalid_fgEstinzione"), function () {
                        //     let fgPresenzaDati = (this.resolve(Yup.ref("fgPresenzaDati")) as string) ?? "";
                        //     let tipoImpegno = (this.resolve(Yup.ref("tipologiaImpegno")) as string) ?? "";
                        //     return ValidationUtility.checkTipoImpegno(fgPresenzaDati, tipoImpegno);
                        //}),
                        // })

                        // .label("Tipologia Impegno"),
                        rata: Yup.string()
                            .when(["fgPresenzaDati", "estinzione"], {
                                is: (fgPresenzaDati, estinzione) => {
                                    return fgPresenzaDati === "S" && estinzione === "S";
                                },
                                then: Yup.string()
                                    /*  .required(i18n.t("error_required_rata"))
                               .notOneOf(["", "0", "0,00"], i18n.t("error_required_rata")) */
                                    .test("format", i18n.t("error_invalid_rata"), (val) => {
                                        return this.checkIfImportoIsValid(val);
                                    })
                                    .test(
                                        "min",
                                        i18n.t("error_min_rata").replace("${1}", FormatUtility.currency(10)),
                                        (val) => {
                                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                                            if (value != null) {
                                                return this.checkMinNumber(value, 10);
                                            }
                                            return false;
                                        }
                                    )
                                    .test(
                                        "max",
                                        i18n.t("error_max_rata").replace("${1}", FormatUtility.currency(75000)),
                                        (val) => {
                                            let value = FormatUtility.numberFromNumberWithSeparators(val);
                                            if (value !== undefined) {
                                                return this.checkMaxNumber(value, 75000);
                                            }
                                            return false;
                                        }
                                    ),
                            })
                            .label(i18n.t("error_field_rata")),

                        debitoResiduo: Yup.string()
                            .when(["fgPresenzaDati", "estinzione"], {
                                is: (fgPresenzaDati, estinzione) => {
                                    return (
                                        fgPresenzaDati === "S" &&
                                        estinzione === "S" &&
                                        visualizzaAltriImpegniNelTabPrestiti
                                    );
                                },
                                then: Yup.string().required(i18n.t("error_required_categoriaAmministrativa")),
                            })
                            .label("Categoria Amministrativa"),

                        istitutoFinanziario: Yup.string()
                            .when(["fgPresenzaDati", "estinzione"], {
                                is: (fgPresenzaDati, estinzione) => {
                                    return (
                                        fgPresenzaDati === "S" &&
                                        estinzione === "S" &&
                                        visualizzaAltriImpegniNelTabPrestiti
                                    );
                                },
                                then: Yup.string().required(i18n.t("error_required_categoriaAmministrativa")),
                            })
                            .label("Categoria Amministrativa"),
                    })
                ),
            }),
        });
    }
    //  /*    public static getSchemaRichiestaPreventivo<T extends object>(fgAltriFinanziamenti: boolean): ValidationSchema<T> {
    //         ValidationUtility.setLocale();
    //         if (fgAltriFinanziamenti === true) {
    //             return Yup.object().shape({
    //                 categoriaAmministrativa: Yup.string()
    //                     .required(i18n.t("error_required_categoriaAmministrativa"))
    //                     .label("Categoria Amministrativa"),
    //                 fgAltriFinanziamenti: Yup.string()
    //                     .required(i18n.t("error_required_fgAltriFinanziamenti"))
    //                     .label("Altri Finanziamenti"),
    //                 altriImpegni: Yup.array().of(
    //                     Yup.object().shape({
    //                         tipologiaImpegno: Yup.string()
    //                             .required(i18n.t("error_required_tipoImpegno"))
    //                             .label("Tipologia Impegno"),
    //                         fgPresenzaDati: Yup.string()
    //                             .required(i18n.t("error_required_fgPresenzaDati"))
    //                             .label("Nuovo Impegno"),
    //                         estinzione: Yup.string()
    //                             .required(i18n.t("error_required_fgEstinzione"))
    //                             .test("checkTipoImpegno", i18n.t("error_invalid_fgEstinzione"), function () {
    //                                 let fgPresenzaDati = (this.resolve(Yup.ref("fgPresenzaDati")) as string) ?? "";
    //                                 let tipoImpegno = (this.resolve(Yup.ref("tipologiaImpegno")) as string) ?? "";
    //                                 return ValidationUtility.checkTipoImpegno(fgPresenzaDati, tipoImpegno);
    //                             })
    //                             .label("Tipologia Impegno"),
    //                         rata: Yup.string()
    //                             /*  .required(i18n.t("error_required_rata"))
    //                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_rata")) */
    //                             .test("format", i18n.t("error_invalid_rata"), (val) => {
    //                                 return this.checkIfImportoIsValid(val);
    //                             })
    //                             .test(
    //                                 "min",
    //                                 i18n.t("error_min_rata").replace("${1}", FormatUtility.currency(10)),
    //                                 (val) => {
    //                                     let value = FormatUtility.numberFromNumberWithSeparators(val);
    //                                     if (value != null) {
    //                                         return this.checkMinNumber(value, 10);
    //                                     }
    //                                     return false;
    //                                 }
    //                             )
    //                             .test(
    //                                 "max",
    //                                 i18n.t("error_max_rata").replace("${1}", FormatUtility.currency(75000)),
    //                                 (val) => {
    //                                     let value = FormatUtility.numberFromNumberWithSeparators(val);
    //                                     if (value !== undefined) {
    //                                         return this.checkMaxNumber(value, 75000);
    //                                     }
    //                                     return false;
    //                                 }
    //                             )
    //                             .label(i18n.t("error_field_rata")),
    //                     })
    //                 ),
    //             });
    //         } else {
    //             return Yup.object().shape({
    //                 categoriaAmministrativa: Yup.string()
    //                     .required(i18n.t("error_required_categoriaAmministrativa"))
    //                     .label("Categoria Amministrativa"),
    //                 fgAltriFinanziamenti: Yup.string()
    //                     .required(i18n.t("error_required_fgAltriFinanziamenti"))
    //                     .label("Altri Finanziamenti"),
    //             });
    //         }
    //     } */

    public static getSchemaAccesso<T extends object>(): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            username: Yup.string()
                .required(i18n.t("error_lbl_username_obbligatorio"))
                .email(i18n.t("error_lbl_email_non_valida"))
                .label("username"),
            password: Yup.string().required(i18n.t("error_lbl_password_obbligatoria")).label("password"),
        });
    }

    public static getSchemaRecuperoPassword<T extends object>(): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            username: Yup.string()
                .required(i18n.t("error_lbl_username_obbligatorio"))
                .email(i18n.t("error_lbl_email_non_valida"))
                .label("username"),
        });
    }

    public static getSchemaRegistrazione<T extends object>(): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            email: Yup.string()
                .email(i18n.t("registrazione_invalid_email"))
                .required(i18n.t("registrazione_required_email"))
                .label("registrazione_field_email")
                .default(""),
            privacy: Yup.array().of(
                Yup.object().shape({
                    descrizione: Yup.string(),
                    isObbligatorio: Yup.boolean(),
                    codice: Yup.string(),
                    isAccettato: Yup.boolean()
                        .when("isObbligatorio", {
                            is: (isObbligatorio) => {
                                return isObbligatorio;
                            },
                            then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                                fieldSchema.test(
                                    "obbligatorio",
                                    i18n.t("registrazione_required_consenso_privacy"),
                                    (val) => {
                                        return FormatUtility.castValue(val, true) === true;
                                    }
                                ),
                        })
                        .label(i18n.t("registrazione_field_consenso_privacy")),
                })
            ),
        });
    }

    public static getSchemaRegistrazioneConfermaEmail<T extends object>(): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            confermaPassword: Yup.string()
                .required(i18n.t("global_campo_obbligatorio"))
                .test("format", i18n.t("confermapage-err-passformato"), (val) => {
                    return this.checkIfPasswordIsValid(val);
                })
                //.oneOf([Yup.ref("repeatPassword"), null], i18n.t("confermapage-err-regconferma"))
                .label("Password"),
            repeatPassword: Yup.string()
                .required(i18n.t("global_campo_obbligatorio"))
                .test("format", i18n.t("confermapage-err-passformato"), (val) => {
                    return this.checkIfPasswordIsValid(val);
                })
                .oneOf([Yup.ref("confermaPassword"), null], i18n.t("confermapage-err-regconferma"))
                .label("Conferma Password"),
        });
    }

    public static getSchemaRinvioMail<T extends object>(): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            username: Yup.string()
                .required(i18n.t("error_lbl_username_obbligatorio"))
                .email(i18n.t("error_lbl_email_non_valida"))
                .label("username"),
        });
    }

    public static getSchemaCambioPassword<T extends object>(): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            username: Yup.string()
                .required(i18n.t("error_lbl_username_obbligatorio"))
                .email(i18n.t("error_lbl_email_non_valida"))
                .label("username"),
            password: Yup.string()
                .required(i18n.t("global_campo_obbligatorio"))
                .test("format", i18n.t("confermapage-err-passformato"), (val) => {
                    return this.checkIfPasswordIsValid(val);
                })
                //.oneOf([Yup.ref("repeatPassword"), null], i18n.t("confermapage-err-regconferma"))
                .label("Password"),
            confermaPassword: Yup.string()
                .required(i18n.t("global_campo_obbligatorio"))
                .test("format", i18n.t("confermapage-err-passformato"), (val) => {
                    return this.checkIfPasswordIsValid(val);
                })
                .oneOf([Yup.ref("password"), null], i18n.t("confermapage-err-regconferma"))
                .label("Conferma Password"),
        });
    }
    public static getSchemaRichiestaTabDatiAnagrafici<T extends object>(
        fullValidation: boolean,
        isPensionato: boolean,
        visualizzaCampiBonifico: boolean,
        attivaAdeguataVerifica: boolean
    ): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            nome: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_nome")),
                })
                .test("format", i18n.t("error_invalid_nome"), (val) => {
                    return this.checkIfNotContainsNumbers(val);
                })
                .test("format", i18n.t("error_invalid_nome"), (val) => {
                    return this.checkIfContainsEuropeanChar(val);
                })
                .label("Nome"),
            cognome: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_cognome")),
                })
                .test("format", i18n.t("error_invalid_cognome"), (val) => {
                    return this.checkIfNotContainsNumbers(val);
                })
                .test("format", i18n.t("error_invalid_nome"), (val) => {
                    return this.checkIfContainsEuropeanChar(val);
                })
                .label("Cognome"),
            dataNascita: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_data_nascita")),
                })
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("min", i18n.t("error_min_data_nascita").replace("${1}", (86).toString()), (val) => {
                    let minDate = new Date();
                    minDate.setFullYear(minDate.getFullYear() - 86);
                    return this.checkMinDate(val, minDate);
                })
                .test("futura", i18n.t("error_futura_data_nascita"), (val) => {
                    let maxDate = new Date();
                    return this.checkMaxDate(val, maxDate);
                })
                .test("minorenne", i18n.t("error_minorenne_data_nascita"), (val) => {
                    let maxDate = new Date();
                    maxDate.setFullYear(maxDate.getFullYear() - 18);
                    return this.checkMaxDate(val, maxDate);
                })
                .label("Data Nascita"),
            sesso: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_sesso")),
                })
                .label("Sesso"),
            codiceFiscale: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_codice_fiscale")),
                })
                .max(16, i18n.t("error_max_codice_fiscale"))
                .test("checkCodiceFiscale", i18n.t("error_invalid_codice_fiscale"), function (val) {
                    let cognome = (this.resolve(Yup.ref("cognome")) as string) ?? "";
                    let nome = (this.resolve(Yup.ref("nome")) as string) ?? "";
                    let dataNascita = (this.resolve(Yup.ref("dataNascita")) as string) ?? "";
                    let sesso = (this.resolve(Yup.ref("sesso")) as string) ?? "";
                    let luogoNascita = (this.resolve(Yup.ref("luogoNascita")) as string) ?? "";
                    let nazioneNascita = (this.resolve(Yup.ref("nazioneNascita")) as string) ?? ""
                    let estero: boolean = nazioneNascita !== environment.codNazioneItalia
                    return ValidationUtility.checkCodiceFiscale(cognome, nome, dataNascita, sesso, estero ? nazioneNascita : luogoNascita, val);
                })
                .label("Codice Fiscale"),
            nazioneNascita: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_nazione_nascita")),
                })
                .label("Nazione di nascita"),
            luogoNascita: Yup.string()
                .when("nazioneNascita", {
                    is: (nazioneNascita) => {
                        return nazioneNascita === environment.codNazioneItalia && fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_luogo_nascita")),
                })
                .label("Luogo di Nascita"),
            cap: Yup.string()
                .when("fullValidation", {
                    is: (nazioneNascita) => {
                        return nazioneNascita === environment.codNazioneItalia && fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_cap")),
                })
                .label("Cap"),
            luogoNascitaEstero: Yup.string()
                .when("nazioneNascita", {
                    is: (nazioneNascita) => {
                        return nazioneNascita !== environment.codNazioneItalia && fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_luogo_nascita")),
                })
                .max(50)
                .label("Luogo di Nascita"),
            cittadinanza: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_cittadinanza")),
                })
                .label("Cittadinanza"),
            altraCittadinanza: Yup.string()
                .when("swtCittadinanzaSecondaria", {
                    is: (swtCittadinanzaSecondaria) => {
                        return FormatUtility.castValue(swtCittadinanzaSecondaria, true) === true;
                    },
                    then: Yup.string().required(i18n.t("error_required_cittadinanza_secondaria")),
                })
                .label("Cittadinanza secondaria"),

            tipoDocumento: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_tipo_documento")),
                })
                .label("Tipo Documento"),
            numeroDocumento: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_numero_documento")),
                })
                .label("Numero Documento"),
            luogoRilascio: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_luogo_rilascio")),
                })
                .label("Luogo Rilascio"),
            dataRilascioDoc: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_data_rilascio")),
                })
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("futura", i18n.t("error_futura_data_rilascio"), (val) => {
                    let maxDate = new Date();
                    return this.checkMaxDate(val, maxDate);
                })
                .label("Data Rilascio"),
            dataScadenzaDoc: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_data_sacadenza")),
                })
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("min", i18n.t("error_min_data_scadenza"), function (val) {
                    let minDate = new Date();
                    return ValidationUtility.checkMinDate(val, minDate, true);
                })
                .test("min", i18n.t("error_range_data_rilascio_scadenza"), function (val) {
                    let minDate = this.resolve(Yup.ref("dataRilascioDoc"));
                    if (minDate === undefined) {
                        return true;
                    }
                    return ValidationUtility.checkMinDate(val, minDate);
                })
                /* .test("range", i18n.t("error_range_data_scadenza"), function (val) {
                    let tipoDocumento = (this.resolve(Yup.ref("tipoDocumento")) as string) ?? "";
                    switch (tipoDocumento) {
                        case codCartaIdentita:
                            let dataRilascio = (this.resolve(Yup.ref("dataRilascioDoc")) as string) ?? "";
                            let dataNascita = (this.resolve(Yup.ref("dataNascita")) as string) ?? "";
                            return ValidationUtility.checkRangeDataScadenza(val, dataRilascio, dataNascita);
                        default:
                            return true;
                    }
                }) */
                .label("Data Scadenza"),

            indirizzoResidenza: Yup.object().shape({
                nazione: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_nazione")),
                    })
                    .label("Stato residenza"),
                codiceIndirizzo: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_tipo_indirizzo")),
                    })
                    .label("Tipo indirizzo residenza"),
                indirizzo: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_indirizzo")),
                    })
                    .max(50)
                    .label("Indirizzo residenza"),
                civico: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_civico")),
                    })
                    .max(50)
                    .label("Civico residenza"),
                luogoEstero: Yup.string()
                    .when("nazione", {
                        is: (nazione) => {
                            return nazione !== environment.codNazioneItalia && fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_localita")),
                    })
                    .label("Comune residenza"),
                comune: Yup.string()
                    .when("nazione", {
                        is: (nazione) => {
                            return nazione === environment.codNazioneItalia && fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_localita")),
                    })
                    .label("Comune residenza"),
                cap: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_cap")),
                    })
                    .label("Cap residenza"),
            }),

            indirizzoDomicilio: Yup.object().when("swtDomicilio", {
                is: (swtDomicilio) => {
                    return FormatUtility.castValue(swtDomicilio, true) === true;
                },
                then: Yup.object().shape({
                    nazione: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_nazione")),
                        })
                        .label("Stato domicilio"),
                    codiceIndirizzo: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_tipo_indirizzo")),
                        })
                        .label("Tipo indirizzo domicilio"),
                    indirizzo: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_indirizzo")),
                        })
                        .max(50)
                        .label("Indirizzo domicilio"),
                    civico: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_civico")),
                        })
                        .max(50)
                        .label("Civico domicilio"),
                    luogoEstero: Yup.string()
                        .when("nazione", {
                            is: (nazione) => {
                                return nazione !== environment.codNazioneItalia && fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_localita")),
                        })
                        .label("Comune domicilio"),
                    comune: Yup.string()
                        .when("nazione", {
                            is: (nazione) => {
                                return nazione === environment.codNazioneItalia && fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_localita")),
                        })
                        .label("Comune domicilio"),
                    cap: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_cap")),
                        })
                        .label("Cap domicilio"),
                }),
            }),

            indirizzoCorrispondenza: Yup.object().when("swtCorrispondenza", {
                is: (swtCorrispondenza) => {
                    return FormatUtility.castValue(swtCorrispondenza, true) === true;
                },
                then: Yup.object().shape({
                    nazione: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_nazione")),
                        })
                        .label("Stato corrispondenza"),
                    codiceIndirizzo: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_tipo_indirizzo")),
                        })
                        .label("Tipo indirizzo corrispondenza"),
                    indirizzo: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_indirizzo")),
                        })
                        .max(50)
                        .label("Indirizzo corrispondenza"),
                    civico: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_civico")),
                        })
                        .max(50)
                        .label("Civico corrispondenza"),
                    luogoEstero: Yup.string()
                        .when("nazione", {
                            is: (nazione) => {
                                return nazione !== environment.codNazioneItalia && fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_localita")),
                        })
                        .label("Comune corrispondenza"),
                    comune: Yup.string()
                        .when("nazione", {
                            is: (nazione) => {
                                return nazione === environment.codNazioneItalia && fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_localita")),
                        })
                        .label("Comune corrispondenza"),
                    cap: Yup.string()
                        .when("fullValidation", {
                            is: () => {
                                return fullValidation;
                            },
                            then: Yup.string().required(i18n.t("error_required_cap")),
                        })
                        .label("Cap corrispondenza"),
                }),
            }),

            recapitoTelefonico: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: Yup.string().required(i18n.t("error_required_telefono")),
                })
                .test("format", i18n.t("error_invalid_telefono"), (val: string) => {
                    if (val === undefined) {
                        return true;
                    }
                    return ValidationUtility.checkPhoneNumber(val);
                })
                .label("Telefono"),

            amministrazione: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_amministrazione")),
                })
                .label("Amministrazione"),

            dataScadenzaContrattoDeterminato: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_data_scadenza"), function (val: string) {
                            let isDeterminato = (this.resolve(Yup.ref("isDeterminato")) as boolean) ?? false;
                            if (isDeterminato === false || (isDeterminato && val)) {
                                return true;
                            } else {
                                return false;
                            }
                        }),
                })
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("Data fine contratto"),
            isIndeterminato: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkContratto", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isDeterminato = (this.resolve(Yup.ref("isDeterminato")) as boolean) ?? false;
                            let isIndeterminato = (this.resolve(Yup.ref("isIndeterminato")) as boolean) ?? false;
                            return ValidationUtility.checkContratto(isDeterminato, isIndeterminato);
                        }),
                })
                .label("Tipo di contratto"),

            isDeterminato: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkContratto", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isDeterminato = (this.resolve(Yup.ref("isDeterminato")) as boolean) ?? false;
                            let isIndeterminato = (this.resolve(Yup.ref("isIndeterminato")) as boolean) ?? false;
                            return ValidationUtility.checkContratto(isDeterminato, isIndeterminato);
                        }),
                })
                .label("Tipo di contratto"),
            tipologiaPensioneAltro: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_altro_pensione"), function (val: string) {
                            let isAltro = (this.resolve(Yup.ref("tipologiaPensione.chiave")) as string) ?? "";
                            if (isAltro !== "3" || val) {
                                return true;
                            } else {
                                return false;
                            }
                        }),
                })
                .max(50)
                .label("Tipologia di pensione"),
            isTipologiaPensione: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkPensione", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isTipologiaPensione =
                                (this.resolve(Yup.ref("isTipologiaPensione")) as boolean) ?? false;
                            let isCointestata = (this.resolve(Yup.ref("isCointestata")) as boolean) ?? false;
                            return ValidationUtility.checkPensione(isTipologiaPensione, isCointestata);
                        }),
                })
                .label("Tipologia di pensione"),
            tipologiaPensione: Yup.object().shape({
                chiave: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: (fieldSchema: Yup.MixedSchema<string>) =>
                            fieldSchema.test("required", i18n.t("error_invalid_tipo_contratto"), function (val) {
                                let isTipologiaPensione =
                                    (this.resolve(Yup.ref("isTipologiaPensione")) as boolean) ?? false;
                                if (isTipologiaPensione !== true || val) {
                                    return true;
                                } else {
                                    return false;
                                }
                            }),
                    })
                    .label("Tipologia Pensione"),
                valore: Yup.string().label("Tipologia Pensione"),
            }),
            /*    isVecchiaia: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkPensione", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isVecchiaia = (this.resolve(Yup.ref("isVecchiaia")) as boolean) ?? false;
                            let isInvalidita = (this.resolve(Yup.ref("isInvalidita")) as boolean) ?? false;
                            let isAltro = (this.resolve(Yup.ref("isAltro")) as boolean) ?? false;
                            return ValidationUtility.checkPensione(isVecchiaia, isInvalidita, isAltro);
                        }),
                })
                .label("Tipologia di pensione"),
            isInvalidita: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkPensione", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isVecchiaia = (this.resolve(Yup.ref("isVecchiaia")) as boolean) ?? false;
                            let isInvalidita = (this.resolve(Yup.ref("isInvalidita")) as boolean) ?? false;
                            let isAltro = (this.resolve(Yup.ref("isAltro")) as boolean) ?? false;
                            return ValidationUtility.checkPensione(isVecchiaia, isInvalidita, isAltro);
                        }),
                })
                .label("Tipologia di pensione"),
            isAltro: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkPensione", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isVecchiaia = (this.resolve(Yup.ref("isVecchiaia")) as boolean) ?? false;
                            let isInvalidita = (this.resolve(Yup.ref("isInvalidita")) as boolean) ?? false;
                            let isAltro = (this.resolve(Yup.ref("isAltro")) as boolean) ?? false;
                            return ValidationUtility.checkPensione(isVecchiaia, isInvalidita, isAltro);
                        }),
                })
                .label("Tipologia di pensione"), */
            isCointestata: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkPensione", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isTipologiaPensione =
                                (this.resolve(Yup.ref("isTipologiaPensione")) as boolean) ?? false;
                            let isCointestata = (this.resolve(Yup.ref("isCointestata")) as boolean) ?? false;
                            return ValidationUtility.checkPensione(isTipologiaPensione, isCointestata);
                        }),
                })
                .label("Cointestata"),
            //campo non necessario
            /*  qualifica: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_qualifica")),
                })
                .label("Qualifica"), */

            modalitaErogazione: Yup.object().shape({
                chiave: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: (fieldSchema: Yup.MixedSchema<string>) =>
                            fieldSchema.required(i18n.t("error_required_modalita_erogazione")),
                    })
                    .label("Modalita Erogazione"),
                valore: Yup.string().label("Modalita Erogazione"),
            }),
            iban: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_iban"), function (val: string) {
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.valore")) as string) ?? "";
                            if (modalitaErogazione === "Bonifico") {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })
                .test("format", i18n.t("error_invalid_iban_format"), function (val: string) {
                    if (visualizzaCampiBonifico === true) {
                        let modalitaErogazione = (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                        if (modalitaErogazione === "1" || val) {
                            if (IBAN.isValid(val)) {
                                return true;
                            } else {
                                return false;
                            }
                        } else {
                            return true;
                        }
                    } else {
                        return true;
                    }
                })
                .label("Iban"),

            dataAccensioneContoCorrente: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_data_conto"), function (val: string) {
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            if (modalitaErogazione !== "1" || val) {
                                return true;
                            } else {
                                return false;
                            }
                        }),
                })
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("futura", i18n.t("error_futura_data_accensione_conto"), (val) => {
                    let maxDate = new Date();
                    return this.checkMaxDate(val, maxDate);
                })
                .label("Data acccensione conto"),

            isDocumentazioneBancaria: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkRilevazioneCoordinate",
                            i18n.t("error_invalid_rilevazione_coordinate"),
                            function () {
                                let isDocumentazioneBancaria =
                                    (this.resolve(Yup.ref("isDocumentazioneBancaria")) as boolean) ?? false;
                                let isBustaPaga = (this.resolve(Yup.ref("isBustaPaga")) as boolean) ?? false;
                                let modalitaErogazione =
                                    (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                                let isAltroBonifico = (this.resolve(Yup.ref("isAltroBonifico")) as boolean) ?? false;
                                return ValidationUtility.checkRilevazioneCoordinate(
                                    isDocumentazioneBancaria,
                                    isBustaPaga,
                                    isAltroBonifico,
                                    modalitaErogazione
                                );
                            }
                        ),
                })
                .label("Rilevazione Coordinate"),
            isBustaPaga: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkRilevazioneCoordinate",
                            i18n.t("error_invalid_rilevazione_coordinate"),
                            function () {
                                let isDocumentazioneBancaria =
                                    (this.resolve(Yup.ref("isDocumentazioneBancaria")) as boolean) ?? false;
                                let isBustaPaga = (this.resolve(Yup.ref("isBustaPaga")) as boolean) ?? false;
                                let isAltroBonifico = (this.resolve(Yup.ref("isAltroBonifico")) as boolean) ?? false;
                                let modalitaErogazione =
                                    (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                                return ValidationUtility.checkRilevazioneCoordinate(
                                    isDocumentazioneBancaria,
                                    isBustaPaga,
                                    isAltroBonifico,
                                    modalitaErogazione
                                );
                            }
                        ),
                })
                .label("Rilevazione Coordinate"),
            isAltroBonifico: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkRilevazioneCoordinate",
                            i18n.t("error_invalid_rilevazione_coordinate"),
                            function () {
                                let isDocumentazioneBancaria =
                                    (this.resolve(Yup.ref("isDocumentazioneBancaria")) as boolean) ?? false;
                                let isBustaPaga = (this.resolve(Yup.ref("isBustaPaga")) as boolean) ?? false;
                                let isAltroBonifico = (this.resolve(Yup.ref("isAltroBonifico")) as boolean) ?? false;
                                let modalitaErogazione =
                                    (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                                return ValidationUtility.checkRilevazioneCoordinate(
                                    isDocumentazioneBancaria,
                                    isBustaPaga,
                                    isAltroBonifico,
                                    modalitaErogazione
                                );
                            }
                        ),
                })
                .label("Rilevazione Coordinate"),
            rilevazioneCoordinateAltro: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_altro_coordinate"), function (val: string) {
                            let isAltroBonifico = (this.resolve(Yup.ref("isAltroBonifico")) as boolean) ?? false;
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";

                            if (modalitaErogazione === "1") {
                                if (isAltroBonifico === true && val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                if (val !== undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            }
                        }),
                })
                .label("Rilevazione Coordinate Altro"),
            isContoCointestato: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkIntestatario", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isMeMedesimo = (this.resolve(Yup.ref("isMeMedesimo")) as boolean) ?? false;
                            let isContoCointestato = (this.resolve(Yup.ref("isContoCointestato")) as boolean) ?? false;
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            return ValidationUtility.checkIntestatario(
                                isMeMedesimo,
                                isContoCointestato,
                                modalitaErogazione
                            );
                        }),
                })
                .label("Tipo di contratto"),

            isMeMedesimo: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkIntestatario", i18n.t("error_invalid_tipo_contratto"), function () {
                            let isMeMedesimo = (this.resolve(Yup.ref("isMeMedesimo")) as boolean) ?? false;
                            let isContoCointestato = (this.resolve(Yup.ref("isContoCointestato")) as boolean) ?? false;
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            return ValidationUtility.checkIntestatario(
                                isMeMedesimo,
                                isContoCointestato,
                                modalitaErogazione
                            );
                        }),
                })
                .label("Tipo di contratto"),

            nominativoCointestatario: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test(
                            "required",
                            i18n.t("error_required_nome_intestatario"),
                            function (val: string) {
                                let isContoCointestato =
                                    (this.resolve(Yup.ref("isContoCointestato")) as boolean) ?? false;
                                let modalitaErogazione =
                                    (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                                if (modalitaErogazione === "1") {
                                    if (isContoCointestato === true && val === undefined) {
                                        return false;
                                    } else {
                                        return true;
                                    }
                                } else {
                                    if (val !== undefined) {
                                        return false;
                                    } else {
                                        return true;
                                    }
                                }
                            }
                        ),
                })
                .label("Nominativo Cointestatario"),

            gradoParentelaCointestatario: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_grado_parentela"), function (val: string) {
                            let isContoCointestato = (this.resolve(Yup.ref("isContoCointestato")) as boolean) ?? false;
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";

                            if (modalitaErogazione === "1") {
                                if (isContoCointestato === true && val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                if (val !== undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            }
                        }),
                })
                .label("Grado Parentela"),

            banca: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_banca"), function (val: string) {
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            if (modalitaErogazione !== "1" || val) {
                                return true;
                            } else {
                                return false;
                            }
                        }),
                })
                .label("Banca"),

            sportello: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_sportello"), function (val: string) {
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            if (modalitaErogazione !== "1" || val) {
                                return true;
                            } else {
                                return false;
                            }
                        }),
                })
                .label("Sportello"),
            isOrdinario: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkTipoContoCorrente", i18n.t("error_invalid_tipo_conto"), function () {
                            let isOrdinario = (this.resolve(Yup.ref("isOrdinario")) as boolean) ?? false;
                            let isOnline = (this.resolve(Yup.ref("isOnline")) as boolean) ?? false;
                            let isCartaPrepagata = (this.resolve(Yup.ref("isCartaPrepagata")) as boolean) ?? false;
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            return ValidationUtility.checkTipoContoCorrente(
                                isOrdinario,
                                isOnline,
                                isCartaPrepagata,
                                modalitaErogazione
                            );
                        }),
                })
                .label("Tipo Conto Corrente"),
            isOnline: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkTipoContoCorrente", i18n.t("error_invalid_tipo_conto"), function () {
                            let isOrdinario = (this.resolve(Yup.ref("isOrdinario")) as boolean) ?? false;
                            let isOnline = (this.resolve(Yup.ref("isOnline")) as boolean) ?? false;
                            let isCartaPrepagata = (this.resolve(Yup.ref("isCartaPrepagata")) as boolean) ?? false;
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            return ValidationUtility.checkTipoContoCorrente(
                                isOrdinario,
                                isOnline,
                                isCartaPrepagata,
                                modalitaErogazione
                            );
                        }),
                })
                .label("Tipo Conto Corrente"),
            isCartaPrepagata: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && visualizzaCampiBonifico === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkTipoContoCorrente", i18n.t("error_invalid_tipo_conto"), function () {
                            let isOrdinario = (this.resolve(Yup.ref("isOrdinario")) as boolean) ?? false;
                            let isOnline = (this.resolve(Yup.ref("isOnline")) as boolean) ?? false;
                            let isCartaPrepagata = (this.resolve(Yup.ref("isCartaPrepagata")) as boolean) ?? false;
                            let modalitaErogazione =
                                (this.resolve(Yup.ref("modalitaErogazione.chiave")) as string) ?? "";
                            return ValidationUtility.checkTipoContoCorrente(
                                isOrdinario,
                                isOnline,
                                isCartaPrepagata,
                                modalitaErogazione
                            );
                        }),
                })
                .label("Tipo Conto Corrente"),
            dataAssunzione: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation; //&& isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_data_assunzione")),
                })
                /*  .test("required", i18n.t("error_required_data_assunzione"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                }) */
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .test("min", i18n.t("error_min_data_assunzione").replace("${1}", (86).toString()), (val) => {
                    let minDate = new Date();
                    minDate.setFullYear(minDate.getFullYear() - 86);
                    return this.checkMinDate(val, minDate);
                })
                .test("futura", i18n.t("error_futura_data_assunzione"), (val) => {
                    let maxDate = new Date();
                    return this.checkMaxDate(val, maxDate);
                })
                .label(isPensionato === false ? "Data Assunzione" : "Data Pensionamento"),
            isAffitto: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkProprietaAbitazione",
                            i18n.t("error_invalid_proprieta_abitazione"),
                            function () {
                                let isAffitto = (this.resolve(Yup.ref("isAffitto")) as boolean) ?? false;
                                let isProprieta = (this.resolve(Yup.ref("isProprieta")) as boolean) ?? false;
                                let isTerzi = (this.resolve(Yup.ref("isTerzi")) as boolean) ?? false;
                                let isFamiglia = (this.resolve(Yup.ref("isFamiglia")) as boolean) ?? false;
                                return ValidationUtility.checkProprietaAbitazione(
                                    isAffitto,
                                    isProprieta,
                                    isTerzi,
                                    isFamiglia
                                );
                            }
                        ),
                })
                .label("Proprieta Abitazione"),
            isProprieta: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkProprietaAbitazione",
                            i18n.t("error_invalid_proprieta_abitazione"),
                            function () {
                                let isAffitto = (this.resolve(Yup.ref("isAffitto")) as boolean) ?? false;
                                let isProprieta = (this.resolve(Yup.ref("isProprieta")) as boolean) ?? false;
                                let isTerzi = (this.resolve(Yup.ref("isTerzi")) as boolean) ?? false;
                                let isFamiglia = (this.resolve(Yup.ref("isFamiglia")) as boolean) ?? false;
                                return ValidationUtility.checkProprietaAbitazione(
                                    isAffitto,
                                    isProprieta,
                                    isTerzi,
                                    isFamiglia
                                );
                            }
                        ),
                })
                .label("Proprieta Abitazione"),
            isTerzi: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkProprietaAbitazione",
                            i18n.t("error_invalid_proprieta_abitazione"),
                            function () {
                                let isAffitto = (this.resolve(Yup.ref("isAffitto")) as boolean) ?? false;
                                let isProprieta = (this.resolve(Yup.ref("isProprieta")) as boolean) ?? false;
                                let isTerzi = (this.resolve(Yup.ref("isTerzi")) as boolean) ?? false;
                                let isFamiglia = (this.resolve(Yup.ref("isFamiglia")) as boolean) ?? false;
                                return ValidationUtility.checkProprietaAbitazione(
                                    isAffitto,
                                    isProprieta,
                                    isTerzi,
                                    isFamiglia
                                );
                            }
                        ),
                })
                .label("Proprieta Abitazione"),
            isFamiglia: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica === true;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkProprietaAbitazione",
                            i18n.t("error_invalid_proprieta_abitazione"),
                            function () {
                                let isAffitto = (this.resolve(Yup.ref("isAffitto")) as boolean) ?? false;
                                let isProprieta = (this.resolve(Yup.ref("isProprieta")) as boolean) ?? false;
                                let isTerzi = (this.resolve(Yup.ref("isTerzi")) as boolean) ?? false;
                                let isFamiglia = (this.resolve(Yup.ref("isFamiglia")) as boolean) ?? false;
                                return ValidationUtility.checkProprietaAbitazione(
                                    isAffitto,
                                    isProprieta,
                                    isTerzi,
                                    isFamiglia
                                );
                            }
                        ),
                })
                .label("Proprieta Abitazione"),
        });
    }

    public static getSchemaRichiestaTabDatiReddito<T extends object>(
        fullValidation: boolean,
        isPensionato: boolean,
        codiceTipoRichiedente: string,
        visualizzaCampiDatiReddito: boolean,
        attivaAdeguataVerifica: boolean
    ): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            certificatoPensione: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato && visualizzaCampiDatiReddito;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_certificato_pensione")),
                })
                .test("format", i18n.t("error_invalid_certificato_pensione"), (val) => {
                    if (val) {
                        return this.checkIfNumber(val);
                    }
                    return true;
                })
                .max(8, i18n.t("error_maxlength_certificato_pensione").replace("${1}", "8"))
                .label("Certificato pensione"),
            codiceSedePensione: Yup.string()
                /*    .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_codice_sede_pensione")),
                }) */
                .test("format", i18n.t("error_invalid_codice_sede_pensione"), (val) => {
                    if (val) {
                        return this.checkIfNumber(val);
                    }
                    return true;
                })
                .max(4, i18n.t("error_maxlength_codice_sede_pensione").replace("${1}", "4"))
                .label("Codice sede pensione"),
            codiceCategPensione: Yup.string()
                /*  .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_codice_categoria_pensione")),
                })
 */ .test("format", i18n.t("error_invalid_codice_categoria_pensione"), (val) => {
                if (val) {
                    return this.checkIfNotContainsNumbers(val);
                }
                return true;
            })
                .max(3, i18n.t("error_maxlength_codice_categoria_pensione").replace("${1}", "3"))
                .label("Codice categoria pensione"),
            tfrMaturato: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false && codiceTipoRichiedente === "DPR";
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_tfr_maturato"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_tfr_maturato")),
                })
                .test("format", i18n.t("error_invalid_tfr_maturato"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("TFR Maturato"),
            tfrDataRiferimento: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false && codiceTipoRichiedente === "DPR";
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_tfr_data_riferimento")),
                })
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("TFR Data Riferimento"),
            //campo non obbligatorio
            tfrFondo: Yup.string()
                /* .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_tfr_fondo"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_tfr_fondo")),
                }) */
                .test("format", i18n.t("error_invalid_tfr_fondo"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("TFR Fondo"),
            //campo non obbligatorio
            tfrFondoDataRiferimento: Yup.string()
                /*  .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_tfr_fondo_data_riferimento")),
                }) */
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("TFR Fondo Data Riferimento"),
            //campo non obbligatorio
            descrizioneFondo: Yup.string()
                /*  .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_fondo")),
                })  */
                .label("descrizioneFondo"),
            numeroCedolino: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return (
                            fullValidation &&
                            visualizzaCampiDatiReddito &&
                            isPensionato === false &&
                            codiceTipoRichiedente === "DPU"
                        );
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(
                            i18n.t(
                                codiceTipoRichiedente === environment.codTipoRichiedenteDipendentePrivato
                                    ? "error_required_numero_cedolino"
                                    : "error_required_id_stipendio"
                            )
                        ),
                })
                /*  .test(
                    "format",
                    i18n.t(
                        codiceTipoRichiedente === environment.codTipoRichiedenteDipendentePrivato
                            ? "error_invalid_numero_cedolino"
                            : "error_invalid_id_stipendio"
                    ),
                    (val) => {
                        if (val) {
                            return this.checkIfContainsEuropeanChar(val);
                        }
                        return true;
                    }
                ) */
                .max(
                    codiceTipoRichiedente === environment.codTipoRichiedenteDipendentePrivato ? 15 : 8,
                    i18n
                        .t(
                            codiceTipoRichiedente === environment.codTipoRichiedenteDipendentePrivato
                                ? "error_maxlength_numero_cedolino"
                                : "error_maxlength_id_stipendio"
                        )
                        .replace(
                            "${1}",
                            codiceTipoRichiedente === environment.codTipoRichiedenteDipendentePrivato ? "15" : "8"
                        )
                )
                .label(
                    codiceTipoRichiedente === environment.codTipoRichiedenteDipendentePrivato
                        ? "Numero Cedolino"
                        : "ID Stipendio"
                ),
            meseRetribuzione: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && isPensionato === false;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.required(i18n.t("error_required_mese_retribuzione")),
                })
                .test("format", i18n.t("error_global_date_format"), (val) => {
                    if (val) {
                        val = "01/" + val;
                    }
                    return this.checkIfDateIsValidOrUndefined(val);
                })
                .label("Mese Retribuzione"),
            redditoAnnuo: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_reddito_annuo"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_reddito_annuo")),
                })
                .test("format", i18n.t("error_invalid_reddito_annuo"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("Reddito annuo"),
            speseAffittoAnnue: Yup.string()
                /*   .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_spese_affitto_annue"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_spese_affitto_annue")),
                }) */
                .test("format", i18n.t("error_invalid_spese_affitto_annue"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("Spese Affitto Annue"),
            totaleRedditoAnnuo: Yup.string()
                /* .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_totale_reddito_annuo"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_totale_reddito_annuo")),
                }) */
                .test("format", i18n.t("error_invalid_totale_reddito_annuo"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("Totale Reddito Annuo"),
            altreRateizzazioniAnnue: Yup.string()
                /*  .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_altre_reteizzazioni_annue"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_altre_reteizzazioni_annue")),
                }) */
                .test("format", i18n.t("error_invalid_altre_reteizzazioni_annue"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("Altre Rateizzazioni Annue"),
            nettoMesile: Yup.string()
                /*  .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_netto_mensile"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_netto_mensile")),
                }) */
                .test("format", i18n.t("error_invalid_netto_mensile"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("Netto Mesile"),
            altriRedditiRichiedente: Yup.string()
                /*  .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema
                            .required(i18n.t("error_required_altri_redditi_richiedente"))
                            .notOneOf(["", "0", "0,00"], i18n.t("error_required_altri_redditi_richiedente")),
                }) */
                .test("format", i18n.t("error_invalid_altri_redditi_richiedente"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("Netto Mesile"),
            altriRedditiNucleoFam: Yup.string()
                //.when("fullValidation", {
                //    is: () => {
                //        return fullValidation;
                //    },
                //    then: (fieldSchema: Yup.MixedSchema<string>) =>
                //        fieldSchema
                //            .required(i18n.t("error_required_altri_redditi_nucleo_familiare"))
                //            .notOneOf(["", "0", "0,00"], i18n.t("error_required_altri_redditi_nucleo_familiare")),
                //})
                .test("format", i18n.t("error_invalid_altri_redditi_nucleo_familiare"), (val) => {
                    if (val) {
                        return this.checkIfImportoIsValid(val);
                    }
                    return true;
                })
                .label("Altri redditi nucleo familiare"),
            fgDichiarazione: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    // then: Yup.boolean().required(i18n.t("error_required_fgDichiarazione")),
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("obbligatorio", i18n.t("registrazione_required_consenso_privacy"), (val) => {
                            return FormatUtility.castValue(val, true) === true;
                        }),
                })
                .label("Dichiarazione"),
            isFabbricati: Yup.boolean(),
            numeroFabbricati: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rata"), function (val: string) {
                            let isFabbricati = (this.resolve(Yup.ref("isFabbricati")) as boolean) ?? false;
                            if (isFabbricati === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })
                .label("Numero Fabbricati"),
            destinazioneFabbricati: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rata"), function (val: string) {
                            let isFabbricati = (this.resolve(Yup.ref("isFabbricati")) as boolean) ?? false;
                            if (isFabbricati === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })
                .label("Destinazione Fabbricati"),
            isTerreni: Yup.boolean(),
            numeroTerreni: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rata"), function (val: string) {
                            let isTerreni = (this.resolve(Yup.ref("isTerreni")) as boolean) ?? false;
                            if (isTerreni === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })
                .label("Numero Terreni"),
            destinazioneTerreni: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rata"), function (val: string) {
                            let isTerreni = (this.resolve(Yup.ref("isTerreni")) as boolean) ?? false;
                            if (isTerreni === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })
                .label("Destinazioni Terreni"),
            /* fgFinanziamentiInCorso:Yup.boolean(),
                rataFinanziamento:Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rata"), function (val: string) {
                            let fgFinanziamentiInCorso =
                                (this.resolve(Yup.ref("fgFinanziamentiInCorso")) as boolean) ?? false;
                            if (fgFinanziamentiInCorso === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })               
                .label("Rata Finanziamento"),
                finanziatore:Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica ;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_finanziatore"), function (val: string) {
                            let fgFinanziamentiInCorso =
                                (this.resolve(Yup.ref("fgFinanziamentiInCorso")) as boolean) ?? false;
                            if (fgFinanziamentiInCorso === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })               
                .label("Finanziatore"),
                dataInizioFinanziamento:Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_data_finanziamento"), function (val: string) {
                            let fgFinanziamentiInCorso =
                                (this.resolve(Yup.ref("fgFinanziamentiInCorso")) as boolean) ?? false;
                            if (fgFinanziamentiInCorso === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })               
                .label("Data Inizio Finanziamento"),
                rateResidueFinanziamento:Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica ;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rate_residue_finanziamento"), function (val: string) {
                            let fgFinanziamentiInCorso =
                                (this.resolve(Yup.ref("fgFinanziamentiInCorso")) as boolean) ?? false;
                            if (fgFinanziamentiInCorso === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })               
                .label("Rate residue Finanziamento"),
                fgPignoramentiInCorso:Yup.boolean(),
                rataMesilePignoramento:Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica ;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rata"), function (val: string) {
                            let fgPignoramentiInCorso =
                                (this.resolve(Yup.ref("fgPignoramentiInCorso")) as boolean) ?? false;
                            if (fgPignoramentiInCorso === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })               
                .label("Rata Pignoramento"),
                creditore:Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_creditore"), function (val: string) {
                            let fgPignoramentiInCorso =
                                (this.resolve(Yup.ref("fgPignoramentiInCorso")) as boolean) ?? false;
                            if (fgPignoramentiInCorso === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })               
                .label("Creditore"),               
                rateResiduePignoramento:Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica ;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test("required", i18n.t("error_required_rate_residue_pignoramento"), function (val: string) {
                            let fgPignoramentiInCorso =
                                (this.resolve(Yup.ref("fgPignoramentiInCorso")) as boolean) ?? false;
                            if (fgPignoramentiInCorso === true) {
                                if (val === undefined) {
                                    return false;
                                } else {
                                    return true;
                                }
                            } else {
                                return true;
                            }
                        }),
                })               
                .label("Rate residue Pignoramento"), 
            isSupera: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkSuperaPercentuale",
                            i18n.t("error_invalid_supera_percentuale"),
                            function () {
                                let isSupera = (this.resolve(Yup.ref("isSupera")) as boolean) ?? false;
                                let isNonSupera = (this.resolve(Yup.ref("isNonSupera")) as boolean) ?? false;
                                return ValidationUtility.checkSuperaPercentuale(isSupera, isNonSupera);
                            }
                        ),
                })
                .label("Supera Percentuale"),
            isNonSupera: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkSuperaPercentuale",
                            i18n.t("error_invalid_supera_percentuale"),
                            function () {
                                let isSupera = (this.resolve(Yup.ref("isSupera")) as boolean) ?? false;
                                let isNonSupera = (this.resolve(Yup.ref("isNonSupera")) as boolean) ?? false;
                                return ValidationUtility.checkSuperaPercentuale(isSupera, isNonSupera);
                            }
                        ),
                })
                .label("Supera Percentuale"),*/
            isRenditaFinanziaria: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkFonteReddito", i18n.t("error_invalid_supera_percentuale"), function () {
                            let altriRedditiNucleoFam =
                                (this.resolve(Yup.ref("altriRedditiNucleoFam")) as string) ?? "";
                            let altriRedditiRichiedente =
                                (this.resolve(Yup.ref("altriRedditiRichiedente")) as string) ?? "";
                            let isRenditaFinanziaria =
                                (this.resolve(Yup.ref("isRenditaFinanziaria")) as boolean) ?? false;
                            let isRedditoImmobili = (this.resolve(Yup.ref("isRedditoImmobili")) as boolean) ?? false;
                            let isRedditoAltro = (this.resolve(Yup.ref("isRedditoAltro")) as boolean) ?? false;
                            return ValidationUtility.checkFonteReddito(
                                altriRedditiNucleoFam,
                                isRenditaFinanziaria,
                                isRedditoImmobili,
                                isRedditoAltro,
                                altriRedditiRichiedente
                            );
                        }),
                })
                .label("Fonte Reddito"),
            isRedditoImmobili: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkFonteReddito", i18n.t("error_invalid_supera_percentuale"), function () {
                            let altriRedditiNucleoFam =
                                (this.resolve(Yup.ref("altriRedditiNucleoFam")) as string) ?? "";
                            let altriRedditiRichiedente =
                                (this.resolve(Yup.ref("altriRedditiRichiedente")) as string) ?? "";
                            let isRenditaFinanziaria =
                                (this.resolve(Yup.ref("isRenditaFinanziaria")) as boolean) ?? false;
                            let isRedditoImmobili = (this.resolve(Yup.ref("isRedditoImmobili")) as boolean) ?? false;
                            let isRedditoAltro = (this.resolve(Yup.ref("isRedditoAltro")) as boolean) ?? false;
                            return ValidationUtility.checkFonteReddito(
                                altriRedditiNucleoFam,
                                isRenditaFinanziaria,
                                isRedditoImmobili,
                                isRedditoAltro,
                                altriRedditiRichiedente
                            );
                        }),
                })
                .label("Fonte Reddito"),
            isRedditoAltro: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation && attivaAdeguataVerifica;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkFonteReddito", i18n.t("error_invalid_supera_percentuale"), function () {
                            let altriRedditiNucleoFam =
                                (this.resolve(Yup.ref("altriRedditiNucleoFam")) as string) ?? "";
                            let altriRedditiRichiedente =
                                (this.resolve(Yup.ref("altriRedditiRichiedente")) as string) ?? "";
                            let isRenditaFinanziaria =
                                (this.resolve(Yup.ref("isRenditaFinanziaria")) as boolean) ?? false;
                            let isRedditoImmobili = (this.resolve(Yup.ref("isRedditoImmobili")) as boolean) ?? false;
                            let isRedditoAltro = (this.resolve(Yup.ref("isRedditoAltro")) as boolean) ?? false;
                            return ValidationUtility.checkFonteReddito(
                                altriRedditiNucleoFam,
                                isRenditaFinanziaria,
                                isRedditoImmobili,
                                isRedditoAltro,
                                altriRedditiRichiedente
                            );
                        }),
                })
                .label("Fonte Reddito"),
        });
    }

    public static getSchemaRichiestaTabAltriPrestiti<T extends object>(
        fullValidation: boolean,
        attivaAdeguataVerifica: boolean,
        visualizzaAltriImpegniNelTabPrestiti: boolean
    ): ValidationSchema<T> {
        ValidationUtility.setLocale();

        if (visualizzaAltriImpegniNelTabPrestiti) {
            return Yup.object().shape({
                fgAltriImpegni: Yup.boolean().label("Altri prestiti"),
                fgEstinzioneTerzi: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_estinzione_terzi")),
                    })
                    .label("Estinzione Terzi"),
                isSupera: Yup.boolean()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation && attivaAdeguataVerifica;
                        },
                        then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                            fieldSchema.test(
                                "checkSuperaPercentuale",
                                i18n.t("error_invalid_supera_percentuale"),
                                function () {
                                    let isSupera = (this.resolve(Yup.ref("isSupera")) as boolean) ?? false;
                                    let isNonSupera = (this.resolve(Yup.ref("isNonSupera")) as boolean) ?? false;
                                    return ValidationUtility.checkSuperaPercentuale(isSupera, isNonSupera);
                                }
                            ),
                    })
                    .label("Supera Percentuale"),
                isNonSupera: Yup.boolean()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation && attivaAdeguataVerifica;
                        },
                        then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                            fieldSchema.test(
                                "checkSuperaPercentuale",
                                i18n.t("error_invalid_supera_percentuale"),
                                function () {
                                    let isSupera = (this.resolve(Yup.ref("isSupera")) as boolean) ?? false;
                                    let isNonSupera = (this.resolve(Yup.ref("isNonSupera")) as boolean) ?? false;
                                    return ValidationUtility.checkSuperaPercentuale(isSupera, isNonSupera);
                                }
                            ),
                    })
                    .label("Supera Percentuale"),
                altriImpegni: Yup.array().when("fgAltriImpegni", {
                    is: true,
                    then: Yup.array()
                        .of(
                            Yup.object().shape({
                                tipologiaImpegno: Yup.string()
                                    .required(i18n.t("error_required_tipoImpegno"))
                                    .label("Tipologia Impegno"),
                                rata: Yup.string()
                                    .when(["fgPresenzaDati", "estinzione"], {
                                        is: (fgPresenzaDati, estinzione) => {
                                            return fgPresenzaDati === "S" && estinzione === "S";
                                        },
                                        then: Yup.string()
                                            /*  .required(i18n.t("error_required_rata"))
                                       .notOneOf(["", "0", "0,00"], i18n.t("error_required_rata")) */
                                            .test("format", i18n.t("error_invalid_rata"), (val) => {
                                                return this.checkIfImportoIsValid(val);
                                            })
                                            .test(
                                                "min",
                                                i18n.t("error_min_rata").replace("${1}", FormatUtility.currency(10)),
                                                (val) => {
                                                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                                                    if (value != null) {
                                                        return this.checkMinNumber(value, 10);
                                                    }
                                                    return false;
                                                }
                                            )
                                            .test(
                                                "max",
                                                i18n.t("error_max_rata").replace("${1}", FormatUtility.currency(75000)),
                                                (val) => {
                                                    let value = FormatUtility.numberFromNumberWithSeparators(val);
                                                    if (value !== undefined) {
                                                        return this.checkMaxNumber(value, 75000);
                                                    }
                                                    return false;
                                                }
                                            ),
                                    })
                                    .label(i18n.t("error_field_rata")),

                                debitoResiduo: Yup.string()
                                    .when(["fgPresenzaDati", "estinzione"], {
                                        is: (fgPresenzaDati, estinzione) => {
                                            return (
                                                fgPresenzaDati === "S" &&
                                                estinzione === "S" &&
                                                visualizzaAltriImpegniNelTabPrestiti
                                            );
                                        },
                                        then: Yup.string().required(i18n.t("error_required_categoriaAmministrativa")),
                                    })
                                    .label("Categoria Amministrativa"),

                                istitutoFinanziario: Yup.string()
                                    .when(["fgPresenzaDati", "estinzione"], {
                                        is: (fgPresenzaDati, estinzione) => {
                                            return (
                                                fgPresenzaDati === "S" &&
                                                estinzione === "S" &&
                                                visualizzaAltriImpegniNelTabPrestiti
                                            );
                                        },
                                        then: Yup.string().required(i18n.t("error_required_categoriaAmministrativa")),
                                    })
                                    .label("Categoria Amministrativa"),
                            })
                        )
                        .label("Altri Impegni"),
                }),
            });
        } else {
            return Yup.object().shape({
                fgAltriImpegni: Yup.boolean().label("Altri prestiti"),
                fgEstinzioneTerzi: Yup.string()
                    .when("fullValidation", {
                        is: () => {
                            return fullValidation;
                        },
                        then: Yup.string().required(i18n.t("error_required_estinzione_terzi")),
                    })
                    .label("Estinzione Terzi"),
                //         altriImpegni: Yup.array().of(
                //             Yup.object().shape({
                //                 tipologiaImpegno: Yup.string()
                //                     .required(i18n.t("error_required_tipoImpegno"))
                //                     .label("Tipologia Impegno"),
                //                 fgPresenzaDati: Yup.string()
                //                     .required(i18n.t("error_required_fgPresenzaDati"))
                //                     .label("Nuovo Impegno"),
                //                 estinzione: Yup.string()
                //                     .required(i18n.t("error_required_fgEstinzione"))
                //                     .test("checkTipoImpegno", i18n.t("error_invalid_fgEstinzione"), function () {
                //                         let fgPresenzaDati = (this.resolve(Yup.ref("fgPresenzaDati")) as string) ?? "";
                //                         let tipoImpegno = (this.resolve(Yup.ref("tipologiaImpegno")) as string) ?? "";
                //                         return ValidationUtility.checkTipoImpegno(fgPresenzaDati, tipoImpegno);
                //                     })
                //                     .label("Tipologia Impegno"),
                //                 rata: Yup.string()
                //                     /*  .required(i18n.t("error_required_rata"))
                //                    .notOneOf(["", "0", "0,00"], i18n.t("error_required_rata")) */
                //                     .test("format", i18n.t("error_invalid_rata"), (val) => {
                //                         return this.checkIfImportoIsValid(val);
                //                     })
                //                     .test(
                //                         "min",
                //                         i18n.t("error_min_rata").replace("${1}", FormatUtility.currency(10)),
                //                         (val) => {
                //                             let value = FormatUtility.numberFromNumberWithSeparators(val);
                //                             if (value != null) {
                //                                 return this.checkMinNumber(value, 10);
                //                             }
                //                             return false;
                //                         }
                //                     )
                //                     .test(
                //                         "max",
                //                         i18n.t("error_max_rata").replace("${1}", FormatUtility.currency(75000)),
                //                         (val) => {
                //                             let value = FormatUtility.numberFromNumberWithSeparators(val);
                //                             if (value !== undefined) {
                //                                 return this.checkMaxNumber(value, 75000);
                //                             }
                //                             return false;
                //                         }
                //                     )
                //                     .label(i18n.t("error_field_rata")),
                //             })
                //         ),
            });
        }
    }

    public static getSchemaRichiestaTabAdeguataVerifica<T extends object>(
        fullValidation: boolean,
        attivaAdeguataVerifica: boolean
    ): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            isClienteBanca: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkRapportoBanca", i18n.t("error_invalid_rapporto_banca"), function () {
                            let isClienteBanca = (this.resolve(Yup.ref("isClienteBanca")) as boolean) ?? false;
                            let isClienteAgenzia = (this.resolve(Yup.ref("isClienteAgenzia")) as boolean) ?? false;
                            let isNewCliente = (this.resolve(Yup.ref("isNewCliente")) as boolean) ?? false;
                            return ValidationUtility.checkRapportoBanca(isClienteBanca, isClienteAgenzia, isNewCliente);
                        }),
                })
                .label("Rapporto Banca"),
            isClienteAgenzia: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkRapportoBanca", i18n.t("error_invalid_rapporto_banca"), function () {
                            let isClienteBanca = (this.resolve(Yup.ref("isClienteBanca")) as boolean) ?? false;
                            let isClienteAgenzia = (this.resolve(Yup.ref("isClienteAgenzia")) as boolean) ?? false;
                            let isNewCliente = (this.resolve(Yup.ref("isNewCliente")) as boolean) ?? false;
                            return ValidationUtility.checkRapportoBanca(isClienteBanca, isClienteAgenzia, isNewCliente);
                        }),
                })
                .label("Rapporto Banca"),
            isNewCliente: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkRapportoBanca", i18n.t("error_invalid_rapporto_banca"), function () {
                            let isClienteBanca = (this.resolve(Yup.ref("isClienteBanca")) as boolean) ?? false;
                            let isClienteAgenzia = (this.resolve(Yup.ref("isClienteAgenzia")) as boolean) ?? false;
                            let isNewCliente = (this.resolve(Yup.ref("isNewCliente")) as boolean) ?? false;
                            return ValidationUtility.checkRapportoBanca(isClienteBanca, isClienteAgenzia, isNewCliente);
                        }),
                })
                .label("Rapporto Banca"),
            isCelibeNubile: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkStatoCivile", i18n.t("error_invalid_stato_civile"), function () {
                            let isCelibeNubile = (this.resolve(Yup.ref("isCelibeNubile")) as boolean) ?? false;
                            let isConvivente = (this.resolve(Yup.ref("isConvivente")) as boolean) ?? false;
                            let isConiugato = (this.resolve(Yup.ref("isConiugato")) as boolean) ?? false;
                            let isDivorziatoSeparato =
                                (this.resolve(Yup.ref("isDivorziatoSeparato")) as boolean) ?? false;
                            let isVedovo = (this.resolve(Yup.ref("isVedovo")) as boolean) ?? false;
                            return ValidationUtility.checkStatoCivile(
                                isCelibeNubile,
                                isConvivente,
                                isConiugato,
                                isDivorziatoSeparato,
                                isVedovo
                            );
                        }),
                })
                .label("Stato Civile"),
            isConvivente: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkStatoCivile", i18n.t("error_invalid_stato_civile"), function () {
                            let isCelibeNubile = (this.resolve(Yup.ref("isCelibeNubile")) as boolean) ?? false;
                            let isConvivente = (this.resolve(Yup.ref("isConvivente")) as boolean) ?? false;
                            let isConiugato = (this.resolve(Yup.ref("isConiugato")) as boolean) ?? false;
                            let isDivorziatoSeparato =
                                (this.resolve(Yup.ref("isDivorziatoSeparato")) as boolean) ?? false;
                            let isVedovo = (this.resolve(Yup.ref("isVedovo")) as boolean) ?? false;
                            return ValidationUtility.checkStatoCivile(
                                isCelibeNubile,
                                isConvivente,
                                isConiugato,
                                isDivorziatoSeparato,
                                isVedovo
                            );
                        }),
                })
                .label("Stato Civile"),
            isConiugato: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkStatoCivile", i18n.t("error_invalid_stato_civile"), function () {
                            let isCelibeNubile = (this.resolve(Yup.ref("isCelibeNubile")) as boolean) ?? false;
                            let isConvivente = (this.resolve(Yup.ref("isConvivente")) as boolean) ?? false;
                            let isConiugato = (this.resolve(Yup.ref("isConiugato")) as boolean) ?? false;
                            let isDivorziatoSeparato =
                                (this.resolve(Yup.ref("isDivorziatoSeparato")) as boolean) ?? false;
                            let isVedovo = (this.resolve(Yup.ref("isVedovo")) as boolean) ?? false;
                            return ValidationUtility.checkStatoCivile(
                                isCelibeNubile,
                                isConvivente,
                                isConiugato,
                                isDivorziatoSeparato,
                                isVedovo
                            );
                        }),
                })
                .label("Stato Civile"),
            isDivorziatoSeparato: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkStatoCivile", i18n.t("error_invalid_stato_civile"), function () {
                            let isCelibeNubile = (this.resolve(Yup.ref("isCelibeNubile")) as boolean) ?? false;
                            let isConvivente = (this.resolve(Yup.ref("isConvivente")) as boolean) ?? false;
                            let isConiugato = (this.resolve(Yup.ref("isConiugato")) as boolean) ?? false;
                            let isDivorziatoSeparato =
                                (this.resolve(Yup.ref("isDivorziatoSeparato")) as boolean) ?? false;
                            let isVedovo = (this.resolve(Yup.ref("isVedovo")) as boolean) ?? false;
                            return ValidationUtility.checkStatoCivile(
                                isCelibeNubile,
                                isConvivente,
                                isConiugato,
                                isDivorziatoSeparato,
                                isVedovo
                            );
                        }),
                })
                .label("Stato Civile"),
            isVedovo: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test("checkStatoCivile", i18n.t("error_invalid_stato_civile"), function () {
                            let isCelibeNubile = (this.resolve(Yup.ref("isCelibeNubile")) as boolean) ?? false;
                            let isConvivente = (this.resolve(Yup.ref("isConvivente")) as boolean) ?? false;
                            let isConiugato = (this.resolve(Yup.ref("isConiugato")) as boolean) ?? false;
                            let isDivorziatoSeparato =
                                (this.resolve(Yup.ref("isDivorziatoSeparato")) as boolean) ?? false;
                            let isVedovo = (this.resolve(Yup.ref("isVedovo")) as boolean) ?? false;
                            return ValidationUtility.checkStatoCivile(
                                isCelibeNubile,
                                isConvivente,
                                isConiugato,
                                isDivorziatoSeparato,
                                isVedovo
                            );
                        }),
                })
                .label("Stato Civile"),
            isComunioneDeiBeni: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkRegimePatrimoniale",
                            i18n.t("error_invalid_regime_patrimoniale"),
                            function () {
                                let isComunioneDeiBeni =
                                    (this.resolve(Yup.ref("isComunioneDeiBeni")) as boolean) ?? false;
                                let isSeparazioneDeiBeni =
                                    (this.resolve(Yup.ref("isSeparazioneDeiBeni")) as boolean) ?? false;
                                return ValidationUtility.checkRegimePatrimoniale(
                                    isComunioneDeiBeni,
                                    isSeparazioneDeiBeni
                                );
                            }
                        ),
                })
                .label("Regime Patrimoniale"),
            isSeparazioneDeiBeni: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkRegimePatrimoniale",
                            i18n.t("error_invalid_regime_patrimoniale"),
                            function () {
                                let isComunioneDeiBeni =
                                    (this.resolve(Yup.ref("isComunioneDeiBeni")) as boolean) ?? false;
                                let isSeparazioneDeiBeni =
                                    (this.resolve(Yup.ref("isSeparazioneDeiBeni")) as boolean) ?? false;
                                return ValidationUtility.checkRegimePatrimoniale(
                                    isComunioneDeiBeni,
                                    isSeparazioneDeiBeni
                                );
                            }
                        ),
                })
                .label("Regime Patrimoniale"),
            numeroNucleoFamiliare: Yup.string()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<string>) =>
                        fieldSchema.test(
                            "required",
                            i18n.t("error_required_componenti_nucleo"),
                            function (val: string) {
                                if (val) {
                                    return true;
                                } else {
                                    return false;
                                }
                            }
                        ),
                })
                .label("Numero Componenti Nucleo Familiare"),
            /*   isTitolareFinanziamento: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkTitolareEffetivo",
                            i18n.t("error_invalid_titolare_effettivo"),
                            function () {
                                let isTitolareFinanziamento =
                                    (this.resolve(Yup.ref("isTitolareFinanziamento")) as boolean) ?? false;
                                let isnoPEP = (this.resolve(Yup.ref("isnoPEP")) as boolean) ?? false;
                                let isEsecutoreFinanziamento =
                                    (this.resolve(Yup.ref("isEsecutoreFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkTitolareEffettivo(
                                    isTitolareFinanziamento,
                                    isnoPEP,
                                    isEsecutoreFinanziamento
                                );
                            }
                        ),
                })
                .label("Titolare Effettivo"),
            isnoPEP: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkTitolareEffetivo",
                            i18n.t("error_invalid_titolare_effettivo"),
                            function () {
                                let isTitolareFinanziamento =
                                    (this.resolve(Yup.ref("isTitolareFinanziamento")) as boolean) ?? false;
                                let isnoPEP = (this.resolve(Yup.ref("isnoPEP")) as boolean) ?? false;
                                let isEsecutoreFinanziamento =
                                    (this.resolve(Yup.ref("isEsecutoreFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkTitolareEffettivo(
                                    isTitolareFinanziamento,
                                    isnoPEP,
                                    isEsecutoreFinanziamento
                                );
                            }
                        ),
                })
                .label("Titolare Effettivo"),
            isEsecutoreFinanziamento: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkTitolareEffetivo",
                            i18n.t("error_invalid_titolare_effettivo"),
                            function () {
                                let isTitolareFinanziamento =
                                    (this.resolve(Yup.ref("isTitolareFinanziamento")) as boolean) ?? false;
                                let isnoPEP = (this.resolve(Yup.ref("isnoPEP")) as boolean) ?? false;
                                let isEsecutoreFinanziamento =
                                    (this.resolve(Yup.ref("isEsecutoreFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkTitolareEffettivo(
                                    isTitolareFinanziamento,
                                    isnoPEP,
                                    isEsecutoreFinanziamento
                                );
                            }
                        ),
                })
                .label("Titolare Effettivo"), */
            isCqs: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkTipoFinanziamento",
                            i18n.t("error_invalid_tipo_finanziamento"),
                            function () {
                                let isCqs = (this.resolve(Yup.ref("isCqs")) as boolean) ?? false;
                                let isDlg = (this.resolve(Yup.ref("isDlg")) as boolean) ?? false;
                                let isCqp = (this.resolve(Yup.ref("isCqp")) as boolean) ?? false;
                                let isAnticipoFinanziamento =
                                    (this.resolve(Yup.ref("isAnticipoFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkTipoFinanziamento(
                                    isCqs,
                                    isDlg,
                                    isCqp,
                                    isAnticipoFinanziamento
                                );
                            }
                        ),
                })
                .label("Natura Finanziamento"),
            isDlg: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkTipoFinanziamento",
                            i18n.t("error_invalid_tipo_finanziamento"),
                            function () {
                                let isCqs = (this.resolve(Yup.ref("isCqs")) as boolean) ?? false;
                                let isDlg = (this.resolve(Yup.ref("isDlg")) as boolean) ?? false;
                                let isCqp = (this.resolve(Yup.ref("isCqp")) as boolean) ?? false;
                                let isAnticipoFinanziamento =
                                    (this.resolve(Yup.ref("isAnticipoFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkTipoFinanziamento(
                                    isCqs,
                                    isDlg,
                                    isCqp,
                                    isAnticipoFinanziamento
                                );
                            }
                        ),
                })
                .label("Natura Finanziamento"),
            isCqp: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkTipoFinanziamento",
                            i18n.t("error_invalid_tipo_finanziamento"),
                            function () {
                                let isCqs = (this.resolve(Yup.ref("isCqs")) as boolean) ?? false;
                                let isDlg = (this.resolve(Yup.ref("isDlg")) as boolean) ?? false;
                                let isCqp = (this.resolve(Yup.ref("isCqp")) as boolean) ?? false;
                                let isAnticipoFinanziamento =
                                    (this.resolve(Yup.ref("isAnticipoFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkTipoFinanziamento(
                                    isCqs,
                                    isDlg,
                                    isCqp,
                                    isAnticipoFinanziamento
                                );
                            }
                        ),
                })
                .label("Natura Finanziamento"),
            isAnticipoFinanziamento: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkTipoFinanziamento",
                            i18n.t("error_invalid_tipo_finanziamento"),
                            function () {
                                let isCqs = (this.resolve(Yup.ref("isCqs")) as boolean) ?? false;
                                let isDlg = (this.resolve(Yup.ref("isDlg")) as boolean) ?? false;
                                let isCqp = (this.resolve(Yup.ref("isCqp")) as boolean) ?? false;
                                let isAnticipoFinanziamento =
                                    (this.resolve(Yup.ref("isAnticipoFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkTipoFinanziamento(
                                    isCqs,
                                    isDlg,
                                    isCqp,
                                    isAnticipoFinanziamento
                                );
                            }
                        ),
                })
                .label("Natura Finanziamento"),
            isAcquistoBeni: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkScopoFinanziamento",
                            i18n.t("error_invalid_scopo_finanziamento"),
                            function () {
                                let isAcquistoBeni = (this.resolve(Yup.ref("isAcquistoBeni")) as boolean) ?? false;
                                let isEsigenzePersonali =
                                    (this.resolve(Yup.ref("isEsigenzePersonali")) as boolean) ?? false;
                                let isAltroScopoFinanziamento =
                                    (this.resolve(Yup.ref("isAltroScopoFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkScopoFinanziamento(
                                    isAcquistoBeni,
                                    isEsigenzePersonali,
                                    isAltroScopoFinanziamento
                                );
                            }
                        ),
                })
                .label("Scopo Finanziamento"),
            isEsigenzePersonali: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkScopoFinanziamento",
                            i18n.t("error_invalid_scopo_finanziamento"),
                            function () {
                                let isAcquistoBeni = (this.resolve(Yup.ref("isAcquistoBeni")) as boolean) ?? false;
                                let isEsigenzePersonali =
                                    (this.resolve(Yup.ref("isEsigenzePersonali")) as boolean) ?? false;
                                let isAltroScopoFinanziamento =
                                    (this.resolve(Yup.ref("isAltroScopoFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkScopoFinanziamento(
                                    isAcquistoBeni,
                                    isEsigenzePersonali,
                                    isAltroScopoFinanziamento
                                );
                            }
                        ),
                })
                .label("Scopo Finanziamento"),
            isAltroScopoFinanziamento: Yup.boolean()
                .when("fullValidation", {
                    is: () => {
                        return fullValidation;
                    },
                    then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                        fieldSchema.test(
                            "checkScopoFinanziamento",
                            i18n.t("error_invalid_scopo_finanziamento"),
                            function () {
                                let isAcquistoBeni = (this.resolve(Yup.ref("isAcquistoBeni")) as boolean) ?? false;
                                let isEsigenzePersonali =
                                    (this.resolve(Yup.ref("isEsigenzePersonali")) as boolean) ?? false;
                                let isAltroScopoFinanziamento =
                                    (this.resolve(Yup.ref("isAltroScopoFinanziamento")) as boolean) ?? false;
                                return ValidationUtility.checkScopoFinanziamento(
                                    isAcquistoBeni,
                                    isEsigenzePersonali,
                                    isAltroScopoFinanziamento
                                );
                            }
                        ),
                })
                .label("Scopo Finanziamento"),
        });
    }

    public static getSchemaRichiestaTabConcludi<T extends object>(fullValidation: boolean): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            fgDichiarazioneBeneficiario: Yup.boolean().when("fullValidation", {
                is: () => {
                    return fullValidation;
                },
                then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                    fieldSchema.test("obbligatorio", i18n.t("concludi_required"), (val) => {
                        return FormatUtility.castValue(val, true) === true;
                    }),
            }),

            fgDichiarazioneNonEspostaPolitica: Yup.boolean().when("fullValidation", {
                is: () => {
                    return fullValidation;
                },
                then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                    fieldSchema.test("checkPolitico", i18n.t("error_invalid_concludi"), function () {
                        let fgDichiarazioneNonEspostaPolitica =
                            (this.resolve(Yup.ref("fgDichiarazioneNonEspostaPolitica")) as boolean) ?? false;
                        let fgDichiarazioneEspostaPolitica =
                            (this.resolve(Yup.ref("fgDichiarazioneEspostaPolitica")) as boolean) ?? false;
                        return ValidationUtility.checkPolitico(
                            fgDichiarazioneNonEspostaPolitica,
                            fgDichiarazioneEspostaPolitica
                        );
                    }),
            }),

            fgDichiarazioneEspostaPolitica: Yup.boolean().when("fullValidation", {
                is: () => {
                    return fullValidation;
                },
                then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                    fieldSchema.test("checkPolitico", i18n.t("error_invalid_concludi"), function () {
                        let fgDichiarazioneNonEspostaPolitica =
                            (this.resolve(Yup.ref("fgDichiarazioneNonEspostaPolitica")) as boolean) ?? false;
                        let fgDichiarazioneEspostaPolitica =
                            (this.resolve(Yup.ref("fgDichiarazioneEspostaPolitica")) as boolean) ?? false;
                        return ValidationUtility.checkPolitico(
                            fgDichiarazioneNonEspostaPolitica,
                            fgDichiarazioneEspostaPolitica
                        );
                    }),
            }),

            fgDichiarazioneEsigenzePersonali: Yup.boolean().when("fullValidation", {
                is: () => {
                    return fullValidation;
                },
                then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                    fieldSchema.test("checkEsigenzePersonali", i18n.t("error_invalid_concludi"), function () {
                        let fgDichiarazioneEsigenzePersonali =
                            (this.resolve(Yup.ref("fgDichiarazioneEsigenzePersonali")) as boolean) ?? false;
                        let fgDichiarazioneAltro = (this.resolve(Yup.ref("fgDichiarazioneAltro")) as boolean) ?? false;
                        return ValidationUtility.checkEsigenzePersonali(
                            fgDichiarazioneEsigenzePersonali,
                            fgDichiarazioneAltro
                        );
                    }),
            }),

            fgDichiarazioneAltro: Yup.boolean().when("fullValidation", {
                is: () => {
                    return fullValidation;
                },
                then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                    fieldSchema.test("checkEsigenzePersonali", i18n.t("error_invalid_concludi"), function () {
                        let fgDichiarazioneEsigenzePersonali =
                            (this.resolve(Yup.ref("fgDichiarazioneEsigenzePersonali")) as boolean) ?? false;
                        let fgDichiarazioneAltro = (this.resolve(Yup.ref("fgDichiarazioneAltro")) as boolean) ?? false;
                        return ValidationUtility.checkEsigenzePersonali(
                            fgDichiarazioneEsigenzePersonali,
                            fgDichiarazioneAltro
                        );
                    }),
            }),
        });
    }

    public static getSchemaConsensi<T extends object>(): ValidationSchema<T> {
        ValidationUtility.setLocale();
        return Yup.object().shape({
            privacy: Yup.array().of(
                Yup.object().shape({
                    descrizione: Yup.string(),
                    isObbligatorio: Yup.boolean(),
                    codice: Yup.string(),
                    isAccettato: Yup.boolean()
                        .when("isObbligatorio", {
                            is: (isObbligatorio) => {
                                return isObbligatorio;
                            },
                            then: (fieldSchema: Yup.MixedSchema<boolean>) =>
                                fieldSchema.test(
                                    "obbligatorio",
                                    i18n.t("registrazione_required_consenso_privacy"),
                                    (val) => {
                                        return FormatUtility.castValue(val, true) === true;
                                    }
                                ),
                        })
                        .label(i18n.t("registrazione_field_consenso_privacy")),
                })
            ),
        });
    }

    private static checkIfNotContainsNumbers = (value: string) => {
        const regExpress = new RegExp(/[^0-9]/g);
        return regExpress.test(value);
    };

    private static checkIfContainsEuropeanChar = (value: string) => {
        const regExpress = new RegExp(
            /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u
        );
        return regExpress.test(value);
    };

    private static checkIfNumber = (value: string) => {
        const regExpress = new RegExp(/^[0-9]*$/g);
        return regExpress.test(value);
    };

    private static checkIfPasswordIsValid = (password: string) => {
        const regExpress = new RegExp(/^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\W_]).*$/);
        return regExpress.test(password);
    };

    private static checkIfImportoIsValid = (value: number | string): boolean => {
        if (value != null) {
            let number = NaN;
            switch (typeof value) {
                case "number":
                    number = value;
                    break;
                case "string":
                    number = parseFloat(
                        value
                            .toString()
                            .replace(/[^0-9,]/g, "")
                            .replace(",", ".")
                    );
                    break;
            }
            return isNaN(number) === false;
        }
        return false;
    };

    private static checkMinNumber = (value: number, minValue: number, exclude: boolean = false) => {
        if (value != null) {
            if (exclude) {
                return value > minValue;
            } else {
                return value >= minValue;
            }
        }
        return false;
    };

    private static checkMaxNumber = (value: number, maxValue: number, exclude: boolean = false) => {
        if (value != null) {
            if (exclude) {
                return value < maxValue;
            } else {
                return value <= maxValue;
            }
        }
        return false;
    };

    private static checkIfDateIsValidOrUndefined = (date: Date | moment.Moment | string): boolean => {
        if (date === undefined) {
            return true;
        }
        let dateValidated = FormatUtility.dateOrUndefined(date);
        if (dateValidated) {
            return true;
        }
        return false;
    };

    private static checkMinDate = (
        date: Date | moment.Moment | string,
        minDate: Date | moment.Moment | string,
        exclude: boolean = false
    ): boolean => {
        if (date === undefined) {
            return true;
        }
        let dateValidated = FormatUtility.dateOrUndefined(date);
        let minDateValidated = FormatUtility.dateOrUndefined(minDate);
        if (dateValidated && minDateValidated) {
            if (exclude) {
                return dateValidated > minDateValidated;
            } else {
                return dateValidated >= minDateValidated;
            }
        }
        return false;
    };

    private static checkMaxDate = (
        date: Date | moment.Moment | string,
        maxDate: Date | moment.Moment | string,
        exclude: boolean = false
    ): boolean => {
        if (date === undefined) {
            return true;
        }
        let dateValidated = FormatUtility.dateOrUndefined(date);
        let maxDateValidated = FormatUtility.dateOrUndefined(maxDate);
        if (dateValidated && maxDateValidated) {
            if (exclude) {
                return dateValidated < maxDateValidated;
            } else {
                return dateValidated <= maxDateValidated;
            }
        }
        return false;
    };

    private static checkIfCampoIsRequiredByTipoRichiedente = (
        tipiRichiedenti: TipoRichiedenteModel[],
        tipoRichiedenteCodice: string,
        campo: string
    ): boolean => {
        let tipoRichiedente = tipiRichiedenti.find((elem) => elem.codice === tipoRichiedenteCodice);
        if (tipoRichiedente && tipoRichiedente!.campiDiConfigurazione.indexOf(campo) > -1) {
            return true;
        } else {
            return false;
        }
    };

    private static checkIfCampoIsRequiredByConfiguration = (
        campiBoxPreventivo: boolean,
        isObbligatorio: boolean,
        fgMostraLanding: string
    ): boolean => {
        if (isObbligatorio === true && campiBoxPreventivo === true && fgMostraLanding === "S") {
            return true;
        } else {
            return false;
        }
    };

    public static preventEnterKeySubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === "Enter" && event.shiftKey === false) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    public static checkPhoneNumber = (value: string) => {
        let result = isValidPhoneNumber(value);
        if (value && value.startsWith("+39") && value.startsWith("+393") === false) {
            // italian phone number but not mobile phone
            result = false;
        }
        return result;
    };
    public static checkContratto = (isDeterminato: boolean, isIndeterminato: boolean): boolean => {
        if (isDeterminato === false && isIndeterminato === false) {
            return false;
        } else {
            return true;
        }
    };

    public static checkPolitico = (
        fgDichiarazioneNonEspostaPolitica: boolean,
        fgDichiarazioneEspostaPolitica: boolean
    ): boolean => {
        if (fgDichiarazioneNonEspostaPolitica === false && fgDichiarazioneEspostaPolitica === false) {
            return false;
        } else {
            return true;
        }
    };

    public static checkEsigenzePersonali = (
        fgDichiarazioneEsigenzePersonali: boolean,
        fgDichiarazioneAltro: boolean
    ): boolean => {
        if (fgDichiarazioneEsigenzePersonali === false && fgDichiarazioneAltro === false) {
            return false;
        } else {
            return true;
        }
    };

    public static checkPensione = (isTipologiaPensione: boolean, isCointestata: boolean): boolean => {
        if (isTipologiaPensione === false && isCointestata === false) {
            return false;
        } else {
            return true;
        }
    };

    public static checkRilevazioneCoordinate = (
        isDocumentazioneBancaria: boolean,
        isBustaPaga: boolean,
        isAltroBonifico: boolean,
        modalitaErogazione: string
    ): boolean => {
        if (
            isDocumentazioneBancaria === false &&
            isBustaPaga === false &&
            isAltroBonifico === false &&
            modalitaErogazione === "1"
        ) {
            return false;
        } else {
            return true;
        }
    };

    public static checkIntestatario = (
        isMeMedesimo: boolean,
        isContoCointestato: boolean,
        modalitaErogazione: string
    ): boolean => {
        if (isMeMedesimo === false && isContoCointestato === false && modalitaErogazione === "1") {
            return false;
        } else {
            return true;
        }
    };

    public static checkTipoContoCorrente = (
        isOrdinario: boolean,
        isOnline: boolean,
        isCartaPrepagata: boolean,
        modalitaErogazione: string
    ): boolean => {
        if (isOrdinario === false && isOnline === false && isCartaPrepagata === false && modalitaErogazione === "1") {
            return false;
        } else {
            return true;
        }
    };

    public static checkRapportoBanca = (
        isClienteBanca: boolean,
        isClienteAgenzia: boolean,
        isNewCliente: boolean
    ): boolean => {
        if (!isClienteBanca && !isClienteAgenzia && !isNewCliente) {
            return false;
        } else {
            return true;
        }
    };

    public static checkStatoCivile = (
        isCelibeNubile: boolean,
        isConvivente: boolean,
        isConiugato: boolean,
        isDivorziatoSeparato: boolean,
        isVedovo: boolean
    ): boolean => {
        if (!isCelibeNubile && !isConvivente && !isConiugato && !isDivorziatoSeparato && !isVedovo) {
            return false;
        } else {
            return true;
        }
    };

    public static checkRegimePatrimoniale = (isComunioneDeiBeni: boolean, isSeparazioneDeiBeni: boolean): boolean => {
        if (!isSeparazioneDeiBeni && !isComunioneDeiBeni) {
            return false;
        } else {
            return true;
        }
    };

    public static checkTipoFinanziamento = (
        isCqs: boolean,
        isDlg: boolean,
        isCqp: boolean,
        isAnticipoFinanziamento: boolean
    ): boolean => {
        if (!isCqs && !isDlg && !isCqp && !isAnticipoFinanziamento) {
            return false;
        } else {
            return true;
        }
    };

    public static checkScopoFinanziamento = (
        isAcquistoBeni: boolean,
        isEsigenzePersonali: boolean,
        isAltroScopoFinanziamento: boolean
    ): boolean => {
        if (!isAcquistoBeni && !isEsigenzePersonali && !isAltroScopoFinanziamento) {
            return false;
        } else {
            return true;
        }
    };

    public static checkTitolareEffettivo = (
        isTitolareFinanziamento: boolean,
        isnoPEP: boolean,
        isEsecutoreFinanziamento: boolean
    ): boolean => {
        if (!isTitolareFinanziamento && !isnoPEP && !isEsecutoreFinanziamento) {
            return false;
        } else {
            return true;
        }
    };

    public static checkProprietaAbitazione = (
        isAffitto: boolean,
        isProprieta: boolean,
        isTerzi: boolean,
        isFamiglia: boolean
    ): boolean => {
        if (!isAffitto && !isProprieta && !isTerzi && !isFamiglia) {
            return false;
        } else {
            return true;
        }
    };
    public static checkSuperaPercentuale = (isSupera: boolean, isNonSupera: boolean): boolean => {
        if (!isSupera && !isNonSupera) {
            return false;
        } else {
            return true;
        }
    };

    public static checkFonteReddito = (
        altriRedditiNucleoFam: string,
        isRenditaFinanziaria: boolean,
        isRedditoImmobili: boolean,
        isRedditoAltro: boolean,
        altriRedditiRichiedente: string
    ): boolean => {
        if (
            !isRenditaFinanziaria &&
            !isRedditoImmobili &&
            !isRedditoAltro &&
            (altriRedditiNucleoFam !== "" || altriRedditiRichiedente !== "")
        ) {
            return false;
        } else {
            return true;
        }
    };

    public static checkCodiceFiscale = (
        cognome: string,
        nome: string,
        dataNascita: string,
        sesso: string,
        luogoNascita: string,
        codiceFiscale: string
    ): boolean => {

        console.log("luogo nascita calcolo cf  -----------> ", luogoNascita)

        if (codiceFiscale === undefined) {
            return true;
        }
        let sessoFormatted: "M" | "F";
        switch (sesso) {
            case "M":
                sessoFormatted = "M";
                break;
            case "F":
                sessoFormatted = "F";
                break;
            default:
                return false;
        }
        let dateFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataNascita);
        if (dateFormatted === undefined) {
            return false;
        }
        let codiceFiscaleCalcolato = FormatUtility.getCodiceFiscale(
            cognome,
            nome,
            dateFormatted,
            sessoFormatted,
            luogoNascita
        );
        codiceFiscale = codiceFiscale.toUpperCase();
        if (codiceFiscale === codiceFiscaleCalcolato) {
            return true;
        } else if (codiceFiscale.length === 16 && codiceFiscaleCalcolato.length === 16) {
            // controllo per accettare casi di omocodia
            const chPosizioniOmocodia = [14, 13, 12, 10, 9, 7, 6];
            const chValoriOmocodia = "LMNPQRSTUV";
            chPosizioniOmocodia.forEach((chPos: number) => {
                // se il carattere nella posizione corrente del codice fiscale
                // è il carattere applicato in caso di omocodia
                // ignora la posizione nel confronto
                const charCorrente = codiceFiscale.charAt(chPos);
                const charDefault = codiceFiscaleCalcolato.charAt(chPos);
                const numero = parseInt(charDefault);
                if (isNaN(numero) === false) {
                    const charDefaultReplaced = chValoriOmocodia.charAt(numero);
                    if (charCorrente === charDefaultReplaced) {
                        codiceFiscale = codiceFiscale.slice(0, chPos) + codiceFiscale.slice(chPos + 1);
                        codiceFiscaleCalcolato =
                            codiceFiscaleCalcolato.slice(0, chPos) + codiceFiscaleCalcolato.slice(chPos + 1);
                    }
                }
            });
            console.log("codfisc calcolato ----> : ", codiceFiscaleCalcolato)
            if (codiceFiscale === codiceFiscaleCalcolato) {
                return true;
            }
        }
        return false;
    };

    public static getCookie = (cookieName: string): string => {
        const name = cookieName + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    };

    private static checkRangeDataScadenza = (
        dataScadenza: string,
        dataRilascio: string,
        dataNascita: string
    ): boolean => {
        if (dataScadenza === undefined || dataRilascio === undefined || dataNascita === undefined) {
            return true;
        }

        let dataRilascioFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataRilascio);
        let dataScadenzaFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataScadenza);
        let dataNascitaFormatted: Date | undefined = FormatUtility.dateOrUndefined(dataNascita);
        if (
            dataRilascioFormatted === undefined ||
            dataScadenzaFormatted === undefined ||
            dataNascitaFormatted === undefined
        ) {
            return false;
        }
        let day = dataRilascioFormatted.getDate();
        let month = dataRilascioFormatted.getMonth();
        let year = dataRilascioFormatted.getFullYear();

        if (year > 2012) {
            let monthRilascio = dataRilascioFormatted.getMonth();
            // se è maggiore del 2012 bisogna aggiungere 10 anni alla data di rilascio e deve cadere il giorno di nascita
            month = dataNascitaFormatted.getMonth();
            day = dataNascitaFormatted.getDate();
            //se la data di rilascio è antecedente al giorno di nascita se invece è posteriore deve cadere nell'anno successivo
            if (monthRilascio >= month) {
                year += 1;
            }
        }
        let dataScadenzaCorretta = new Date(year + 10, month, day);
        if (dataScadenzaCorretta === dataScadenzaFormatted) {
            return true;
        } else {
            return false;
        }
    };

    public static checkPresenzaDati = (fgPresenzaDati: string): boolean => {
        if (fgPresenzaDati === "S") {
            return true;
        } else {
            return false;
        }
    };

    public static checkTipoImpegno = (fgPresenzaDati: string, tipoImpegno: string): boolean => {
        if (fgPresenzaDati === "S" && tipoImpegno !== "4") {
            return true;
        } else {
            return false;
        }
    };
}
